import React, {
  FC,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useLayoutEffect,
} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Dropdown, MenuProps, Input, Button, Modal, Popover, Tooltip, Spin } from 'antd';
import EmojiPicker, { Theme, EmojiClickData } from 'emoji-picker-react';
import {
  LoadingOutlined,
  SearchOutlined,
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  PlayCircleFilled,
  FileTextOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileZipOutlined,
  FileImageOutlined,
  DownloadOutlined,
  WarningOutlined,
  DeleteOutlined,
  LockOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import {
  doc,
  collection,
  onSnapshot,
  query,
  orderBy,
  limitToLast,
  writeBatch,
  serverTimestamp,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { db, auth } from 'src/config/firebase';
import { sessionStore } from 'src/store/sessionStore';
import { useGetTrainerClients } from 'src/api/mutations/useGetTrainerClients';
import {
  uploadChatAudio,
  uploadChatImage,
  uploadChatVideo,
  uploadChatFile,
  deleteChatMedia,
} from 'src/api/services/firebaseStorage';
import {
  useAudioRecorder,
  AudioRecorderInline,
  AudioPlayer,
  AudioMessageBubble,
} from 'src/components/AudioRecorder';
import { useDebounce } from 'src/hooks/useDebounce';
import {
  useSearchChatMessages,
  fetchMessageContextWindow,
} from 'src/api/queries/useSearchChatMessages';
import { ReportIssueView } from './components/ReportIssueModal/ReportIssueView';
import { MediaLinksFilesView } from './components/MediaLinksFilesView/MediaLinksFilesView';
import { ChatLinkCard } from './components/ChatLinkCard/ChatLinkCard';
import { getInitialLinkMetadata } from './utils/linkMetadata';
import { FullScreenImageModal } from 'src/components/Modals/FullScreenImageModal/FullScreenImageModal';
import { FullScreenVideoModal } from 'src/components/Modals/FullScreenVideoModal/FullScreenVideoModal';
import { MessageReadStatus } from './components/MessageReadStatus';
import { ReactComponent as MoreMenuIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/chat-conversion-menu/messages-more-menu.svg';
import { ReactComponent as MoreMenuCloseIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/chat-conversion-menu/messages-more-menu-close.svg';
import { ReactComponent as ViewClientProfileIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/chat-conversion-menu/messages-view-client-profile.svg';
import { ReactComponent as SearchInConversationIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/chat-conversion-menu/messages-search-in-conversation.svg';
import { ReactComponent as ViewMediaLinksFilesIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/chat-conversion-menu/messages-view-media-links-files.svg';
import { ReactComponent as ReportAnIssueIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/chat-conversion-menu/messages-report-an-issue.svg';
import { ReactComponent as InputAttachmentsIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/chat-input/messages-input-attachments.svg';
import { ReactComponent as InputEmojiIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/chat-input/messages-input-emoji.svg';
import { ReactComponent as InputImagesIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/chat-input/messages-input-images.svg';
import { ReactComponent as InputRecordIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/chat-input/messages-input-record.svg';
import { ReactComponent as InputSendIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/chat-input/messages-input-send.svg';
import { ReactComponent as ResolvedCheckboxCheckedIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/resolved-checkbox-checked.svg';
import { ReactComponent as ResolvedCheckboxUncheckedIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/resolved-checkbox-unchecked.svg';
import { ReactComponent as ChatBackArrowIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/notifications/chat-back-arrow.svg';
import { ReactComponent as SupportHeadsetIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/chat-support-headset.svg';
import {
  ChatMessage,
  ChatMessageAttachment,
  parseFirestoreDate,
  MessagePayloadType,
  FormattedTrainerSchedule,
  ScheduleDayItem,
} from './types';
import { PackageRequestBubble } from './components/PackageRequestBubble/PackageRequestBubble';
import { PackageOfferBubble } from './components/PackageOfferBubble/PackageOfferBubble';
import { PackageActiveBanner } from './components/PackageActiveBanner/PackageActiveBanner';
import { SendFinalOfferModal } from './components/SendFinalOfferModal/SendFinalOfferModal';
import { InviteFreeCallModal } from './components/InviteFreeCallModal/InviteFreeCallModal';
import { FreeCallInvitationBubble } from './components/FreeCallInvitationBubble/FreeCallInvitationBubble';
import { useGetTrainerPackages } from 'src/api/queries/useGetTrainerPackages';
import { useGetTrainerSchedule } from 'src/api/queries/useGetTrainerSchedule';
import type {
  WeeklySchedule,
  DayKey,
} from 'src/screens/OnboardingTrainer/screens/Schedule/utils/timezoneUtils';
import type { TrainerPackage } from 'src/api/services/packagesService';
import './DashboardChatDetails.scss';

interface FirestoreChatParentData {
  isActive?: boolean;
  is_active?: boolean;
  isUnbound?: boolean;
  is_unbound?: boolean;
  unboundAt?: string;
  unbound_at?: string;
  participants?: string[];
  [key: string]: unknown;
}

interface AttachedMediaItem {
  id: string;
  file: File;
  mediaType: 'image' | 'video';
  previewUrl: string;
  storageUrl?: string;
  storagePath?: string;
  isUploading: boolean;
  uploadError?: boolean;
}

const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
  'image/avif',
  'image/svg+xml',
];
const MAX_IMAGE_SIZE_BYTES = 3 * 1024 * 1024; // 3 MB limit

const ALLOWED_VIDEO_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/x-m4v',
  'video/ogg',
  'video/3gpp',
];
const MAX_VIDEO_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB limit

const MAX_MEDIA_COUNT = 5;

interface AttachedDocItem {
  id: string;
  file: File;
  storageUrl?: string;
  storagePath?: string;
  sizeBytes: number;
  isUploading: boolean;
  uploadError?: boolean;
}

const ALLOWED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/rtf',
];
const MAX_DOC_SIZE_BYTES = 3 * 1024 * 1024; // 3 MB limit
const MAX_DOCS_COUNT = 5;

function formatDocFileSize(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const isPureImageExtension = (name?: string, mime?: string): boolean => {
  if (mime?.startsWith('image/')) return true;
  if (!name) return false;
  return /\.(jpe?g|png|webp|gif|avif|heic|heif|svg)$/i.test(name);
};

const isPureVideoExtension = (name?: string, mime?: string): boolean => {
  if (mime?.startsWith('video/')) return true;
  if (!name) return false;
  return /\.(mp4|mov|webm|m4v|ogg|ogv|3gp|mkv|avi)$/i.test(name);
};

const getDocTypeMeta = (fileName?: string, mimeType?: string) => {
  const ext = fileName ? fileName.split('.').pop()?.toLowerCase() : '';
  const mime = mimeType?.toLowerCase() || '';

  if (
    ['mp4', 'mov', 'webm', 'm4v', 'avi', 'mkv'].includes(ext || '') ||
    mime.startsWith('video/')
  ) {
    return {
      icon: <PlayCircleFilled style={{ color: '#f59e0b' }} />,
      bg: 'rgba(245, 158, 11, 0.15)',
      color: '#f59e0b',
      label: ext ? ext.toUpperCase() : 'Video',
    };
  }

  if (ext === 'pdf' || mime === 'application/pdf') {
    return {
      icon: <FilePdfOutlined style={{ color: '#ef4444' }} />,
      bg: 'rgba(239, 68, 68, 0.15)',
      color: '#ef4444',
      label: 'PDF',
    };
  }
  if (
    ['doc', 'docx'].includes(ext || '') ||
    mime.includes('word') ||
    mime.includes('officedocument.wordprocessingml')
  ) {
    return {
      icon: <FileWordOutlined style={{ color: '#3b82f6' }} />,
      bg: 'rgba(59, 130, 246, 0.15)',
      color: '#3b82f6',
      label: 'Word',
    };
  }
  if (
    ['xls', 'xlsx', 'csv'].includes(ext || '') ||
    mime.includes('excel') ||
    mime.includes('spreadsheetml') ||
    mime === 'text/csv'
  ) {
    return {
      icon: <FileExcelOutlined style={{ color: '#10b981' }} />,
      bg: 'rgba(16, 185, 129, 0.15)',
      color: '#10b981',
      label: ext === 'csv' ? 'CSV' : 'Excel',
    };
  }
  if (
    ['ppt', 'pptx'].includes(ext || '') ||
    mime.includes('presentation') ||
    mime.includes('powerpoint')
  ) {
    return {
      icon: <FilePptOutlined style={{ color: '#f97316' }} />,
      bg: 'rgba(249, 115, 22, 0.15)',
      color: '#f97316',
      label: 'PPT',
    };
  }
  if (
    ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext || '') ||
    mime.includes('zip') ||
    mime.includes('compressed')
  ) {
    return {
      icon: <FileZipOutlined style={{ color: '#a855f7' }} />,
      bg: 'rgba(168, 85, 247, 0.15)',
      color: '#a855f7',
      label: 'Archive',
    };
  }
  if (
    ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'].includes(ext || '') ||
    mime.startsWith('image/')
  ) {
    return {
      icon: <FileImageOutlined style={{ color: '#06b6d4' }} />,
      bg: 'rgba(6, 182, 212, 0.15)',
      color: '#06b6d4',
      label: ext ? ext.toUpperCase() : 'Image',
    };
  }
  return {
    icon: <FileTextOutlined style={{ color: '#60a5fa' }} />,
    bg: 'rgba(96, 165, 250, 0.15)',
    color: '#60a5fa',
    label: ext ? ext.toUpperCase() : 'Document',
  };
};

const formatTimeWithAmPm = (timeStr: string) => {
  if (!timeStr) return '';
  if (/AM|PM/i.test(timeStr)) return timeStr;
  const parts = timeStr.split(':');
  if (parts.length < 2) return timeStr;
  let hour = parseInt(parts[0], 10);
  const min = parts[1];
  if (isNaN(hour)) return timeStr;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}:${min} ${ampm}`;
};

export type { ChatMessage, ChatMessageAttachment };

export function formatTrainerWeeklySchedule(
  weeklySchedule?: WeeklySchedule | Record<string, unknown> | null,
  timezone: string = 'Europe/London',
): FormattedTrainerSchedule {
  const dayConfigs: Array<{ key: DayKey; label: string }> = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' },
  ];

  if (!weeklySchedule || typeof weeklySchedule !== 'object') {
    return {
      isUniform: true,
      uniformText: 'Monday to Friday • 09:00 - 17:00',
      daysList: [
        { day: 'Monday', time: '09:00 - 17:00' },
        { day: 'Tuesday', time: '09:00 - 17:00' },
        { day: 'Wednesday', time: '09:00 - 17:00' },
        { day: 'Thursday', time: '09:00 - 17:00' },
        { day: 'Friday', time: '09:00 - 17:00' },
      ],
      timezone,
    };
  }

  const typedSchedule = weeklySchedule as Record<
    string,
    { enabled?: boolean; slots?: Array<{ start?: string; end?: string }> }
  >;
  const activeDaysWithTimes: Array<{
    key: DayKey;
    dayIndex: number;
    label: string;
    timeString: string;
  }> = [];

  dayConfigs.forEach((dayCfg, index) => {
    const dayData = typedSchedule[dayCfg.key];
    if (dayData && dayData.enabled && Array.isArray(dayData.slots) && dayData.slots.length > 0) {
      const validSlots = dayData.slots.filter(
        (s) =>
          typeof s.start === 'string' &&
          typeof s.end === 'string' &&
          Boolean(s.start.trim()) &&
          Boolean(s.end.trim()),
      );
      if (validSlots.length > 0) {
        const sorted = [...validSlots].sort((a, b) => (a.start || '').localeCompare(b.start || ''));
        const timeString = sorted.map((s) => `${s.start} - ${s.end}`).join(', ');
        activeDaysWithTimes.push({
          key: dayCfg.key,
          dayIndex: index,
          label: dayCfg.label,
          timeString,
        });
      }
    }
  });

  if (activeDaysWithTimes.length === 0) {
    return {
      isUniform: true,
      uniformText: 'Monday to Friday • 09:00 - 17:00',
      daysList: [],
      timezone,
    };
  }

  const firstTimeString = activeDaysWithTimes[0].timeString;
  const isUniform = activeDaysWithTimes.every((d) => d.timeString === firstTimeString);

  const daysList: ScheduleDayItem[] = activeDaysWithTimes.map((d) => ({
    day: d.label,
    time: d.timeString,
  }));

  if (isUniform) {
    const isContiguous =
      activeDaysWithTimes.length > 1 &&
      activeDaysWithTimes.every(
        (d, i) => i === 0 || d.dayIndex === activeDaysWithTimes[i - 1].dayIndex + 1,
      );

    let rangeLabel = '';
    if (isContiguous) {
      rangeLabel = `${activeDaysWithTimes[0].label} to ${activeDaysWithTimes[activeDaysWithTimes.length - 1].label}`;
    } else if (activeDaysWithTimes.length === 1) {
      rangeLabel = activeDaysWithTimes[0].label;
    } else {
      rangeLabel = activeDaysWithTimes.map((d) => d.label).join(', ');
    }

    return {
      isUniform: true,
      uniformText: `${rangeLabel} • ${firstTimeString}`,
      daysList,
      timezone,
    };
  }

  return {
    isUniform: false,
    uniformText: undefined,
    daysList,
    timezone,
  };
}

export const DashboardChatDetails: FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inquiryMessageId = searchParams.get('inquiryId');
  const currentUser = sessionStore((state) => state.currentUser);
  const trainerActualUid = auth.currentUser?.uid || currentUser?.uid || '';

  const [clientUid, setClientUid] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [expandedMessageIds, setExpandedMessageIds] = useState<Record<string, boolean>>({});
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  );

  const [isSendOfferModalOpen, setIsSendOfferModalOpen] = useState(false);
  const [isInviteCallModalOpen, setIsInviteCallModalOpen] = useState(false);
  const [selectedRequestMessage, setSelectedRequestMessage] = useState<ChatMessage | null>(null);
  const [isSendingOffer, setIsSendingOffer] = useState(false);
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);

  const { data: trainerPackages = [] } = useGetTrainerPackages(trainerActualUid);
  const publishedPackages = useMemo(
    () => (trainerPackages || []).filter((p) => p.status === 'published'),
    [trainerPackages],
  );

  const { data: scheduleResponse } = useGetTrainerSchedule(trainerActualUid);
  const trainerTimezone =
    scheduleResponse?.data?.timezone ||
    ((currentUser as unknown as Record<string, unknown> | null)?.timezone as string) ||
    'Europe/London';

  const trainerFormattedSchedule = useMemo(() => {
    const weeklySchedule =
      scheduleResponse?.data?.weekly_schedule ||
      ((currentUser as unknown as Record<string, unknown> | null)?.weekly_schedule as
        | WeeklySchedule
        | undefined);
    return formatTrainerWeeklySchedule(weeklySchedule, trainerTimezone);
  }, [scheduleResponse, currentUser, trainerTimezone]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleViewportChange = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      if (mobile) {
        const vv = window.visualViewport;
        const currentHeight = vv ? vv.height : window.innerHeight;
        document.documentElement.style.setProperty(
          '--chat-viewport-height',
          `${Math.round(currentHeight)}px`,
        );

        // Keep page scroll strictly pinned to top (prevents mobile browser from shifting headers)
        if (window.scrollY !== 0 || window.scrollX !== 0) {
          window.scrollTo(0, 0);
        }
        if (document.body.scrollTop !== 0) document.body.scrollTop = 0;
        if (document.documentElement.scrollTop !== 0) document.documentElement.scrollTop = 0;
        const scrollable = document.getElementById('scrollableDashboardMain');
        if (scrollable && scrollable.scrollTop !== 0) {
          scrollable.scrollTop = 0;
        }
      } else {
        document.documentElement.style.removeProperty('--chat-viewport-height');
      }
    };

    handleViewportChange();

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, { passive: true });

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', handleViewportChange);
      vv.addEventListener('scroll', handleViewportChange);
    }

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange);
      if (vv) {
        vv.removeEventListener('resize', handleViewportChange);
        vv.removeEventListener('scroll', handleViewportChange);
      }
      document.documentElement.style.removeProperty('--chat-viewport-height');
    };
  }, []);

  // Prevent pull-to-refresh and isolate scrolling strictly to the messages container on mobile
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let touchStartY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
      const container = messagesContainerRef.current;
      if (container) {
        // Avoid scroll chaining when starting at extremes
        if (container.scrollTop <= 0) {
          container.scrollTop = 1;
        } else if (container.scrollTop + container.clientHeight >= container.scrollHeight) {
          container.scrollTop = container.scrollHeight - container.clientHeight - 1;
        }
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const container = messagesContainerRef.current;
      const isInsideMessages = container && (container === target || container.contains(target));
      const isInsideInput = target.closest('textarea, input, [contenteditable="true"]');

      if (!isInsideMessages && !isInsideInput) {
        // Any touch drag outside messages/inputs is cancelled to keep header and main container fixed
        if (e.cancelable) {
          e.preventDefault();
        }
        return;
      }

      if (isInsideMessages && container) {
        const currentY = e.touches[0].clientY;
        const isDraggingDown = currentY > touchStartY;

        // If at the very top and pulling down, prevent browser pull-to-refresh
        if (isDraggingDown && container.scrollTop <= 1 && e.cancelable) {
          e.preventDefault();
        }
      }
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  const toggleExpandMessage = (msgId: string) => {
    setExpandedMessageIds((prev) => ({
      ...prev,
      [msgId]: !prev[msgId],
    }));
  };

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset before measuring so the textarea can shrink when text is removed.
    textarea.style.height = 'auto';
    const maxHeight = 140;
    const nextHeight = Math.max(24, Math.min(textarea.scrollHeight, maxHeight));
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [inputText]);

  // Attached media (images & videos) state
  const [attachedMedia, setAttachedMedia] = useState<AttachedMediaItem[]>([]);
  const [mediaLimitWarning, setMediaLimitWarning] = useState<string | null>(null);
  const mediaFileInputRef = useRef<HTMLInputElement | null>(null);

  // Attached documents state
  const [attachedDocs, setAttachedDocs] = useState<AttachedDocItem[]>([]);
  const [docLimitWarning, setDocLimitWarning] = useState<string | null>(null);
  const docFileInputRef = useRef<HTMLInputElement | null>(null);

  // Audio Recorder Hook (5-min max, 2-sec debounce)
  const audioRecorder = useAudioRecorder({
    maxDurationSeconds: 300,
    minDurationSeconds: 2,
    onMaxDurationReached: () => {
      toast('Maximum 5-minute recording limit reached.', { icon: '⏱️' });
    },
  });

  // Search state
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [isFetchingContext, setIsFetchingContext] = useState(false);

  const searchOptions = useMemo(
    () => ({
      inquiryId: inquiryMessageId || undefined,
      excludeInquiries: !inquiryMessageId,
    }),
    [inquiryMessageId],
  );

  const { data: searchApiData, isFetching: isSearchingFullHistory } = useSearchChatMessages(
    chatId || '',
    debouncedSearchQuery,
    isSearchActive,
    searchOptions,
  );

  const isSearchActiveRef = useRef(isSearchActive);
  const searchInputRef = useRef<any>(null);
  const attemptedMatchIdsRef = useRef<Set<string>>(new Set());
  const fetchingMatchIdRef = useRef<string | null>(null);
  const stableSearchMatchesRef = useRef<string[]>([]);
  const prevMatchesKeyRef = useRef<string>('');

  useEffect(() => {
    attemptedMatchIdsRef.current.clear();
    fetchingMatchIdRef.current = null;
    prevMatchesKeyRef.current = '';
    stableSearchMatchesRef.current = [];
  }, [debouncedSearchQuery, chatId, inquiryMessageId]);
  useEffect(() => {
    isSearchActiveRef.current = isSearchActive;
    if (isSearchActive) {
      setTimeout(() => {
        searchInputRef.current?.focus?.({ preventScroll: true });
        if (typeof window !== 'undefined') {
          window.scrollTo(0, 0);
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
          const scrollable = document.getElementById('scrollableDashboardMain');
          if (scrollable) scrollable.scrollTop = 0;
        }
      }, 50);
    }
  }, [isSearchActive]);

  const PAGE_SIZE = 10;
  const [messageLimit, setMessageLimit] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const prevKnownMsgIdsRef = useRef<Set<string>>(new Set());
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef<number>(0);
  const prevScrollTopRef = useRef<number>(0);
  const isLoadingEarlierRef = useRef<boolean>(false);
  const anchorMsgIdRef = useRef<string | null>(null);
  const anchorTopOffsetRef = useRef<number>(0);
  const anchorOldOffsetTopRef = useRef<number>(0);
  const isPrependingRef = useRef<boolean>(false);
  const loadCooldownRef = useRef<boolean>(false);
  const prevFirstMsgIdRef = useRef<string | null>(null);

  const [showReportView, setShowReportView] = useState(false);
  const [activeView, setActiveView] = useState<'chat' | 'media_files_links'>('chat');
  const [initialMLFTab, setInitialMLFTab] = useState<'media' | 'files' | 'links'>('media');

  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);

  const [inquiryDoc, setInquiryDoc] = useState<ChatMessage | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isInitialLoadRef = useRef(true);
  const hasUserScrolledRef = useRef<boolean>(false);

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    setMessageLimit(PAGE_SIZE);
    setHasMore(false);
    setLoadingEarlier(false);
    isLoadingEarlierRef.current = false;
    isPrependingRef.current = false;
    loadCooldownRef.current = false;
    prevKnownMsgIdsRef.current = new Set();
    hasUserScrolledRef.current = false;
    isInitialLoadRef.current = true;
    prevFirstMsgIdRef.current = null;
    anchorMsgIdRef.current = null;
    anchorOldOffsetTopRef.current = 0;
  }, [chatId, inquiryMessageId]);

  // Dedicated realtime listener for the specific inquiry document when viewing an inquiry
  useEffect(() => {
    if (!chatId || !inquiryMessageId) {
      setInquiryDoc(null);
      return;
    }

    const inqRef = doc(db, 'flutter_chat_messages', chatId, 'messages', inquiryMessageId);
    const unsubscribe = onSnapshot(
      inqRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setInquiryDoc({
            id: snap.id,
            ...data,
            send_uid: data.send_uid || data.sender_uid || '',
            sender_uid: data.sender_uid || data.send_uid || '',
          } as ChatMessage);
        }
      },
      (err) => {
        console.error('Error listening to inquiry document:', err);
      },
    );

    return () => unsubscribe();
  }, [chatId, inquiryMessageId]);

  // Fallback clientUid from inquiry document if not already determined
  useEffect(() => {
    const inq = inquiryDoc;
    if (inq && trainerActualUid && !clientUid) {
      const senderUid = inq.sender_uid || inq.send_uid;
      const receiveUid = inq.receive_uid;
      const other = senderUid && senderUid !== trainerActualUid ? senderUid : receiveUid;
      if (other && other !== trainerActualUid) {
        setClientUid(other);
      }
    }
  }, [inquiryDoc, trainerActualUid, clientUid]);

  // Query flutter_user for client profile fallback (especially for inquiries from prospective leads)
  const { data: flutterUserData } = useQuery({
    queryKey: ['flutter-client-profile', clientUid],
    queryFn: async () => {
      if (!clientUid) return null;
      try {
        const uSnap = await getDoc(doc(db, 'flutter_user', clientUid));
        if (uSnap.exists()) {
          return uSnap.data() as {
            email?: string;
            name?: string;
            surname?: string;
            avatar_url?: string;
            avatar?: string;
            profile_picture?: string;
          };
        }
      } catch (err) {
        console.warn('Failed to fetch flutter_user:', err);
      }
      return null;
    },
    enabled: !!clientUid,
    staleTime: 5 * 60 * 1000,
  });

  // Get client details from existing hook
  const { data: clientsData } = useGetTrainerClients('all', '', 'name_asc', 1, 1000);
  const clientInfo = clientsData?.clients.find((c) => c.uid === clientUid);
  const [chatParentData, setChatParentData] = useState<FirestoreChatParentData | null>(null);

  const isChatReadOnly = useMemo(() => {
    if (
      chatParentData?.is_unbound === true ||
      chatParentData?.isUnbound === true ||
      chatParentData?.is_active === false ||
      chatParentData?.isActive === false
    ) {
      return true;
    }
    if (clientInfo && (clientInfo.isActive === false || clientInfo.is_active === false)) {
      return true;
    }
    return false;
  }, [chatParentData, clientInfo]);

  useEffect(() => {
    if (!chatId || !trainerActualUid) return;

    // Realtime listener on parent chat document to track participants and read-only status (is_unbound / is_active)
    const parentRef = doc(db, 'flutter_chat_messages', chatId);
    const unsubscribeParent = onSnapshot(
      parentRef,
      (parentSnap) => {
        if (parentSnap.exists()) {
          const pData = parentSnap.data() || {};
          setChatParentData(pData);
          const participants = pData.participants || [];
          const otherUid =
            participants.find((p: string) => p !== trainerActualUid) ||
            participants[0] ||
            trainerActualUid;
          if (otherUid) setClientUid(otherUid);
        } else {
          setChatParentData(null);
          const fallbackUid =
            chatId.split('_').find((id) => id !== trainerActualUid) ||
            chatId.split('_')[0] ||
            trainerActualUid;
          setClientUid(fallbackUid);
        }
      },
      (err) => {
        console.error('Error listening to chat parent document:', err);
      },
    );

    // Realtime Listener on messages subcollection (or inquiry_message subcollection if viewing an inquiry)
    const messagesRef = inquiryMessageId
      ? collection(
          db,
          'flutter_chat_messages',
          chatId,
          'messages',
          inquiryMessageId,
          'inquiry_message',
        )
      : collection(db, 'flutter_chat_messages', chatId, 'messages');
    const q = inquiryMessageId
      ? query(messagesRef, orderBy('send_time', 'asc'))
      : query(messagesRef, orderBy('send_time', 'asc'), limitToLast(messageLimit));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const msgs: ChatMessage[] = [];
        snapshot.forEach((d) => {
          const data = d.data();
          msgs.push({
            id: d.id,
            ...data,
            send_uid: data.send_uid || data.sender_uid || '',
            sender_uid: data.sender_uid || data.send_uid || '',
          } as ChatMessage);
        });

        if (inquiryMessageId) {
          msgs.sort(
            (a, b) =>
              parseFirestoreDate(a.send_time).getTime() - parseFirestoreDate(b.send_time).getTime(),
          );
        }

        // Capture scroll height and scroll top immediately before rendering prepended messages
        if (messagesContainerRef.current) {
          prevScrollHeightRef.current = messagesContainerRef.current.scrollHeight;
          prevScrollTopRef.current = messagesContainerRef.current.scrollTop;
        }

        setMessages((prev) => {
          // Keep optimistic messages that haven't been confirmed by the server yet
          const pendingOptimistic = prev.filter(
            (m) => m.isOptimistic && !msgs.some((sm) => sm.id === m.id),
          );
          const combined = [...msgs, ...pendingOptimistic];

          // Re-sort combined array to ensure optimistic messages fall in correct chronological order
          combined.sort(
            (a, b) =>
              parseFirestoreDate(a.send_time).getTime() - parseFirestoreDate(b.send_time).getTime(),
          );

          return combined;
        });
        setLoading(false);

        // If returned documents count equals messageLimit, there are more earlier messages
        const hasMoreEarlier = !inquiryMessageId && snapshot.docs.length >= messageLimit;
        setHasMore(hasMoreEarlier);

        // Reset trainer unread count on parent document
        const resetUnread = async () => {
          if (snapshot.empty) return;
          try {
            const parentRef = doc(db, 'flutter_chat_messages', chatId);
            const batch = writeBatch(db);
            batch.set(
              parentRef,
              {
                [`unread_count_${trainerActualUid}`]: 0,
              },
              { merge: true },
            );
            await batch.commit();
          } catch (error) {
            console.error('Error resetting unread count:', error);
          }
        };

        resetUnread();
      },
      (error) => {
        console.error('Error listening to messages:', error);
        setLoading(false);
        setLoadingEarlier(false);
        isLoadingEarlierRef.current = false;
        isPrependingRef.current = false;
        loadCooldownRef.current = false;
      },
    );

    return () => {
      unsubscribeParent();
      unsubscribe();
    };
  }, [chatId, trainerActualUid, messageLimit, inquiryMessageId]);

  // In Direct Chat mode: only direct messages (exclude standalone inquiries and inquiry-linked replies)
  // In Inquiry mode: show ONLY the inquiry card and the replies specifically linked to this inquiry
  const displayMessages = useMemo(() => {
    let filtered: ChatMessage[] = [];
    if (!inquiryMessageId) {
      filtered = messages.filter((m) => m.message_type !== 'inquiry' && !m.inquiry_id);
    } else {
      const inqMsg = inquiryDoc;
      const threadReplies = messages.filter((m) => !inqMsg || m.id !== inqMsg.id);
      filtered = inqMsg ? [inqMsg, ...threadReplies] : threadReplies;
    }

    return [...filtered].sort((a, b) => {
      const timeA = parseFirestoreDate(a.send_time).getTime();
      const timeB = parseFirestoreDate(b.send_time).getTime();
      return timeA - timeB;
    });
  }, [messages, inquiryMessageId, inquiryDoc]);

  const lastMessageIdRef = useRef<string>('');

  useLayoutEffect(() => {
    if (!messagesContainerRef.current) return;
    const container = messagesContainerRef.current;

    const currentFirstId = displayMessages[0]?.id || null;
    const prevFirstId = prevFirstMsgIdRef.current;

    // Detect if new older messages were prepended to the rendered messages
    const hasPrependedBatch = Boolean(
      prevFirstId && currentFirstId && prevFirstId !== currentFirstId,
    );

    if (hasPrependedBatch) {
      // Calculate the difference in scroll height before and after the new messages render
      const newScrollHeight = container.scrollHeight;
      const heightDifference = newScrollHeight - prevScrollHeightRef.current;

      // Adjust scrollTop accordingly to keep the scroll position completely stable
      if (heightDifference > 0) {
        const targetScrollTop =
          container.scrollTop > 0
            ? container.scrollTop + heightDifference
            : prevScrollTopRef.current + heightDifference;
        container.scrollTop = targetScrollTop;

        // Double-check on next frame in case of dynamic reflow
        requestAnimationFrame(() => {
          if (container && Math.abs(container.scrollTop - targetScrollTop) > 2) {
            container.scrollTop = targetScrollTop;
          }
        });
      }

      isPrependingRef.current = false;
      isLoadingEarlierRef.current = false;
      anchorMsgIdRef.current = null;
      anchorOldOffsetTopRef.current = 0;

      setTimeout(() => {
        setLoadingEarlier(false);
      }, 350);

      setTimeout(() => {
        loadCooldownRef.current = false;
      }, 600);
    } else if (isInitialLoadRef.current && displayMessages.length > 0) {
      // Initial chat load: jump directly to bottom
      container.scrollTop = container.scrollHeight;
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 400);
    } else if (!isSearchActiveRef.current && displayMessages.length > 0) {
      // Check if a new message arrived at the bottom
      const latestMsg = displayMessages[displayMessages.length - 1];
      if (latestMsg && latestMsg.id !== lastMessageIdRef.current) {
        const isSentByTrainer =
          latestMsg.sender_role === 'trainer' ||
          latestMsg.sender_uid === trainerActualUid ||
          latestMsg.send_uid === trainerActualUid;
        const distanceFromBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight;
        const wasNearBottom = distanceFromBottom < 200;

        if (wasNearBottom || isSentByTrainer) {
          if (isSentByTrainer) {
            container.scrollTop = container.scrollHeight;
          } else {
            container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
          }
        }
      }
    }

    if (displayMessages.length > 0) {
      lastMessageIdRef.current = displayMessages[displayMessages.length - 1].id;
      prevFirstMsgIdRef.current = displayMessages[0].id;
    }
    prevScrollHeightRef.current = container.scrollHeight;
    prevKnownMsgIdsRef.current = new Set(displayMessages.map((m) => m.id));
  }, [displayMessages, trainerActualUid]);

  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;

    // Track when user scrolls up from the bottom
    if (scrollHeight - scrollTop - clientHeight > 80) {
      hasUserScrolledRef.current = true;
    }

    const isScrollable = scrollHeight > clientHeight + 40;

    // Automatically load older messages ONLY when:
    // 1. Content overflows (isScrollable)
    // 2. User has actively scrolled up from bottom
    // 3. User is near the top (< 120px)
    // 4. Older messages exist and initial load is settled
    // 5. Cooldown lock is not active
    if (
      isScrollable &&
      hasUserScrolledRef.current &&
      scrollTop < 120 &&
      hasMore &&
      !loadingEarlier &&
      !isLoadingEarlierRef.current &&
      !loadCooldownRef.current &&
      !isPrependingRef.current &&
      !isInitialLoadRef.current &&
      !loading
    ) {
      const containerRect = messagesContainerRef.current.getBoundingClientRect();
      let anchorId: string | null = null;
      let anchorTopOffset = 0;

      // Find the topmost visible message bubble in viewport before prepending
      for (let i = 0; i < displayMessages.length; i++) {
        const msgEl = document.getElementById(`msg-${displayMessages[i].id}`);
        if (msgEl) {
          const elRect = msgEl.getBoundingClientRect();
          if (elRect.bottom > containerRect.top) {
            anchorId = displayMessages[i].id;
            anchorTopOffset = elRect.top - containerRect.top;
            break;
          }
        }
      }

      if (!anchorId && displayMessages.length > 0) {
        anchorId = displayMessages[0].id;
        const firstEl = document.getElementById(`msg-${displayMessages[0].id}`);
        if (firstEl) {
          anchorTopOffset = firstEl.getBoundingClientRect().top - containerRect.top;
        }
      }

      anchorMsgIdRef.current = anchorId;
      anchorTopOffsetRef.current = anchorTopOffset;
      isPrependingRef.current = true;
      loadCooldownRef.current = true;
      prevScrollHeightRef.current = scrollHeight;
      prevScrollTopRef.current = scrollTop;
      console.log('[ChatScroll] handleScroll TRIGGERED LOAD MORE:', {
        anchorId,
        anchorTopOffset,
        scrollHeight,
        scrollTop,
      });
      isLoadingEarlierRef.current = true;
      setLoadingEarlier(true);
      setMessageLimit((prev) => prev + PAGE_SIZE);

      // Safety timeout: ensure loading indicator never hangs indefinitely
      setTimeout(() => {
        setLoadingEarlier(false);
        isLoadingEarlierRef.current = false;
        isPrependingRef.current = false;
      }, 2000);
    }
  };

  // Viewport detection to mark unread incoming messages as read
  const pendingReadIds = useRef<Set<string>>(new Set());
  const markTimeoutRef = useRef<any | null>(null);

  const flushMarkAsRead = useCallback(async () => {
    if (pendingReadIds.current.size === 0 || !chatId || !trainerActualUid) return;
    const idsToUpdate = Array.from(pendingReadIds.current);
    pendingReadIds.current.clear();

    try {
      const batch = writeBatch(db);
      idsToUpdate.forEach((msgId) => {
        const msgRef = inquiryMessageId
          ? msgId === inquiryMessageId
            ? doc(db, 'flutter_chat_messages', chatId, 'messages', inquiryMessageId)
            : doc(
                db,
                'flutter_chat_messages',
                chatId,
                'messages',
                inquiryMessageId,
                'inquiry_message',
                msgId,
              )
          : doc(db, 'flutter_chat_messages', chatId, 'messages', msgId);

        batch.update(msgRef, {
          is_read: true,
          read_time: serverTimestamp(),
          read_by: trainerActualUid,
        });
      });

      if (!inquiryMessageId) {
        const parentRef = doc(db, 'flutter_chat_messages', chatId);
        batch.set(
          parentRef,
          {
            [`unread_count_${trainerActualUid}`]: 0,
          },
          { merge: true },
        );
      }

      await batch.commit();
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }, [chatId, trainerActualUid, inquiryMessageId]);

  useEffect(() => {
    if (!chatId || !trainerActualUid || messages.length === 0) return;

    const pendingSet = pendingReadIds.current;

    const observer = new IntersectionObserver(
      (entries) => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

        let hasNew = false;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const msgId = entry.target.getAttribute('data-message-id');
            if (msgId) {
              pendingSet.add(msgId);
              hasNew = true;
            }
          }
        });

        if (hasNew) {
          if (markTimeoutRef.current) clearTimeout(markTimeoutRef.current);
          markTimeoutRef.current = setTimeout(flushMarkAsRead, 300);
        }
      },
      { threshold: 0.3 },
    );

    const unreadNodes = document.querySelectorAll(
      '.message-bubble-wrapper.received[data-unread="true"]',
    );
    unreadNodes.forEach((node) => observer.observe(node));

    return () => {
      observer.disconnect();
      if (markTimeoutRef.current) clearTimeout(markTimeoutRef.current);
      pendingSet.clear();
    };
  }, [messages, chatId, trainerActualUid, flushMarkAsRead]);

  // Current inquiry item
  const currentInquiry = useMemo(() => {
    if (inquiryMessageId) {
      return inquiryDoc || messages.find((m) => m.id === inquiryMessageId);
    }
    return undefined;
  }, [messages, inquiryMessageId, inquiryDoc]);

  // Mark inquiry message as read when trainer opens it
  useEffect(() => {
    if (
      inquiryMessageId &&
      currentInquiry &&
      !currentInquiry.is_read &&
      chatId &&
      trainerActualUid
    ) {
      const markInquiryRead = async () => {
        try {
          const inqRef = doc(db, 'flutter_chat_messages', chatId, 'messages', inquiryMessageId);
          await updateDoc(inqRef, {
            is_read: true,
            read_time: serverTimestamp(),
            read_by: trainerActualUid,
          });
        } catch (e) {
          console.error('Error marking inquiry as read:', e);
        }
      };
      markInquiryRead();
    }
  }, [inquiryMessageId, currentInquiry, chatId, trainerActualUid]);

  const isInquiryResolved =
    currentInquiry?.isResolve === true || currentInquiry?.status === 'resolved';
  const [showResolveConfirmModal, setShowResolveConfirmModal] = useState(false);
  const [isUpdatingResolveStatus, setIsUpdatingResolveStatus] = useState(false);

  const handleToggleResolveInquiry = () => {
    setShowResolveConfirmModal(true);
  };

  const handleConfirmToggleResolve = async () => {
    if (!chatId || !inquiryMessageId) return;
    const hasReplies = displayMessages.some((m) => m.id !== inquiryMessageId);
    const nextResolved = !isInquiryResolved;
    const nextStatus = nextResolved ? 'resolved' : hasReplies ? 'replied' : 'reopened';
    try {
      setIsUpdatingResolveStatus(true);
      const msgRef = doc(db, 'flutter_chat_messages', chatId, 'messages', inquiryMessageId);
      const updatePayload: Record<string, any> = {
        isResolve: nextResolved,
        resolve_time: serverTimestamp(),
        status: nextStatus,
        status_updated_at: serverTimestamp(),
        status_updated_by: trainerActualUid,
      };

      if (nextResolved) {
        updatePayload.resolved_at = serverTimestamp();
        updatePayload.resolved_by = trainerActualUid;
      } else {
        updatePayload.resolved_at = null;
        updatePayload.resolved_by = null;
      }

      await updateDoc(msgRef, updatePayload);
      toast.success(nextResolved ? 'Inquiry marked as resolved' : 'Inquiry reopened');
      setShowResolveConfirmModal(false);
    } catch (e) {
      console.error('Error updating inquiry status:', e);
      toast.error('Failed to update inquiry status');
    } finally {
      setIsUpdatingResolveStatus(false);
    }
  };

  // --- Search Logic (Ordered from newest / bottom-most to oldest) ---
  const searchMatches = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return [];
    const qStr = debouncedSearchQuery.toLowerCase();

    let ids: string[] = [];
    if (searchApiData?.matches) {
      // Backend returned results scoped to this conversation mode
      ids = searchApiData.matches.map((m) => m.id);
    } else {
      // Optimistic in-memory matches from currently visible messages while search API is loading
      ids = [...displayMessages]
        .reverse()
        .filter((msg) => {
          const bodyText =
            msg.body || msg.inquiryDetail || msg.inquirySubject || msg.inquiry_text || '';
          if (bodyText.toLowerCase().includes(qStr)) return true;
          if (msg.attachments && Array.isArray(msg.attachments)) {
            return msg.attachments.some(
              (a) => a.file_name && a.file_name.toLowerCase().includes(qStr),
            );
          }
          return false;
        })
        .map((msg) => msg.id);
    }

    const key = ids.join('|');
    if (key === prevMatchesKeyRef.current) {
      return stableSearchMatchesRef.current;
    }
    prevMatchesKeyRef.current = key;
    stableSearchMatchesRef.current = ids;
    return ids;
  }, [searchApiData, displayMessages, debouncedSearchQuery]);

  const jumpToMatch = useCallback(
    async (matchId: string) => {
      if (!matchId) return;

      const scrollToMatchElement = (target: HTMLElement) => {
        const container = messagesContainerRef.current;
        if (!container || !target) return;
        const containerRect = container.getBoundingClientRect();
        const elRect = target.getBoundingClientRect();
        const relativeTop = elRect.top - containerRect.top;
        const targetScrollTop =
          container.scrollTop + relativeTop - containerRect.height / 2 + elRect.height / 2;
        container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
      };

      const el = document.getElementById(`msg-${matchId}`);
      if (el) {
        scrollToMatchElement(el);
        return;
      }

      // If already fetching this match or already attempted and not found in DOM, avoid redundant calls
      if (fetchingMatchIdRef.current === matchId || attemptedMatchIdsRef.current.has(matchId)) {
        return;
      }

      try {
        fetchingMatchIdRef.current = matchId;
        setIsFetchingContext(true);
        const res = await fetchMessageContextWindow(chatId || '', matchId, 25);
        if (res.messages && res.messages.length > 0) {
          setMessages((prev) => {
            const existingIds = new Set(prev.map((m) => m.id));
            const newMsgs = res.messages.filter((m) => !existingIds.has(m.id));
            if (newMsgs.length === 0) return prev;
            const merged = [...prev, ...newMsgs];
            merged.sort(
              (a, b) =>
                parseFirestoreDate(a.send_time).getTime() -
                parseFirestoreDate(b.send_time).getTime(),
            );
            return merged;
          });

          setTimeout(() => {
            const targetEl = document.getElementById(`msg-${matchId}`);
            if (targetEl) {
              scrollToMatchElement(targetEl);
            } else {
              attemptedMatchIdsRef.current.add(matchId);
            }
          }, 100);
        } else {
          attemptedMatchIdsRef.current.add(matchId);
        }
      } catch (err) {
        console.error('Error fetching context window for search match:', err);
        attemptedMatchIdsRef.current.add(matchId);
      } finally {
        fetchingMatchIdRef.current = null;
        setIsFetchingContext(false);
      }
    },
    [chatId],
  );

  useEffect(() => {
    if (isSearchActive && searchMatches.length > 0) {
      const matchId = searchMatches[currentMatchIndex];
      if (matchId) {
        jumpToMatch(matchId);
      }
    }
  }, [currentMatchIndex, searchMatches, isSearchActive, jumpToMatch]);

  const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const renderHighlightedText = (text: string, highlight: string, keyPrefix = 'hl') => {
    if (!text) return null;
    if (!highlight || !highlight.trim()) return text;
    const escaped = escapeRegExp(highlight.trim());
    const regex = new RegExp(`(${escaped})`, 'gi');
    const parts = text.split(regex);
    const cleanHighlight = highlight.trim().toLowerCase();

    return (
      <React.Fragment key={`hl-frag-${keyPrefix}`}>
        {parts.map((part, i) =>
          part.toLowerCase() === cleanHighlight ? (
            <mark key={`${keyPrefix}-mark-${i}`} className="search-highlight">
              {part}
            </mark>
          ) : (
            <React.Fragment key={`${keyPrefix}-text-${i}`}>{part}</React.Fragment>
          ),
        )}
      </React.Fragment>
    );
  };

  // Mobile message length limit before showing Read More
  const MOBILE_MSG_CHAR_LIMIT = 240;

  const extractAllUrls = (text?: string): string[] => {
    if (!text) return [];
    const matches = text.match(/https?:\/\/[^\s]+[^\s.,;:!?'")]|www\.[^\s]+[^\s.,;:!?'")]/gi);
    if (!matches) return [];
    const unique: string[] = [];
    const seen = new Set<string>();
    matches.forEach((m) => {
      const fullUrl = m.startsWith('http') ? m : `https://${m}`;
      const norm = fullUrl.toLowerCase().replace(/\/+$/, '');
      if (!seen.has(norm)) {
        seen.add(norm);
        unique.push(fullUrl);
      }
    });
    return unique;
  };

  // Clickable link renderer with search highlight and mobile Read More / Read Less support
  const renderMessageTextWithLinksAndHighlight = (
    text: string,
    highlight: string,
    msgId?: string,
  ) => {
    if (!text) return null;

    const isExpanded = msgId ? Boolean(expandedMessageIds[msgId]) : false;
    const shouldTruncateOnMobile =
      isMobile && !highlight && text.length > MOBILE_MSG_CHAR_LIMIT && !isExpanded;

    const displayText = shouldTruncateOnMobile
      ? `${text.slice(0, MOBILE_MSG_CHAR_LIMIT).trim()}...`
      : text;

    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
    const parts = displayText.split(urlRegex);

    return (
      <>
        {parts.map((part, i) => {
          if (part.match(urlRegex)) {
            const href = part.startsWith('http') ? part : `https://${part}`;
            return (
              <a
                key={`link-${i}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="chat-clickable-link"
                onClick={(e) => e.stopPropagation()}
              >
                {part}
              </a>
            );
          }
          return (
            <React.Fragment key={`part-${i}`}>
              {renderHighlightedText(part, highlight, `part-${i}`)}
            </React.Fragment>
          );
        })}
        {isMobile && !highlight && text.length > MOBILE_MSG_CHAR_LIMIT && msgId && (
          <button
            type="button"
            className="chat-read-more-btn"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpandMessage(msgId);
            }}
          >
            {isExpanded ? 'Read less' : 'Read more'}
          </button>
        )}
      </>
    );
  };

  const handleSendMessage = async () => {
    if (isChatReadOnly) {
      toast.error('This conversation is read-only.');
      return;
    }

    const hasText = Boolean(inputText.trim());
    const hasAudio = Boolean(audioRecorder.audioBlob);
    const hasMedia = attachedMedia.length > 0;
    const hasDocs = attachedDocs.length > 0;

    if (
      (!hasText && !hasAudio && !hasMedia && !hasDocs) ||
      !chatId ||
      !trainerActualUid ||
      !clientUid
    )
      return;

    if (hasMedia && attachedMedia.some((m) => m.isUploading)) {
      toast('Please wait for photos/videos to finish uploading.', { icon: '⏳' });
      return;
    }

    if (hasDocs && attachedDocs.some((d) => d.isUploading)) {
      toast('Please wait for files to finish uploading.', { icon: '⏳' });
      return;
    }

    if (inputText.length > 2000) {
      toast.error('Message is too long. Please reduce the length.');
      return;
    }

    if (isMobile) {
      textareaRef.current?.blur();
      window.scrollTo(0, 0);
      if (document.body) document.body.scrollTop = 0;
      if (document.documentElement) document.documentElement.scrollTop = 0;
      const scrollable = document.getElementById('scrollableDashboardMain');
      if (scrollable) scrollable.scrollTop = 0;
    }

    setSending(true);
    const textToSend = inputText.trim();
    const recordedAudioBlob = audioRecorder.audioBlob;
    const recordedDuration = audioRecorder.duration;
    const currentAttachedMedia = [...attachedMedia];
    const currentAttachedDocs = [...attachedDocs];

    setInputText('');
    audioRecorder.clearAudio();
    setAttachedMedia([]);
    setAttachedDocs([]);
    setMediaLimitWarning(null);
    setDocLimitWarning(null);

    try {
      const mediaAttachments: ChatMessageAttachment[] = currentAttachedMedia
        .filter((m) => m.storageUrl)
        .map((m) => ({
          kind: 'media',
          type: m.mediaType,
          url: m.storageUrl!,
          storage_path: m.storagePath,
          file_name: m.file.name,
          mime_type: m.file.type || (m.mediaType === 'video' ? 'video/mp4' : 'image/jpeg'),
          size_bytes: m.file.size,
          size_label: formatDocFileSize(m.file.size),
        }));

      const docAttachments: ChatMessageAttachment[] = currentAttachedDocs
        .filter((d) => d.storageUrl)
        .map((d) => ({
          kind: 'file',
          type: 'document',
          url: d.storageUrl!,
          storage_path: d.storagePath,
          file_name: d.file.name,
          mime_type: d.file.type,
          size_bytes: d.sizeBytes,
          size_label: formatDocFileSize(d.sizeBytes),
        }));

      const hasVideos = mediaAttachments.some((m) => m.type === 'video');
      const hasImages = mediaAttachments.some((m) => m.type === 'image');
      const isMediaMessage = mediaAttachments.length > 0;
      const isDocMessage = docAttachments.length > 0;
      const isAudioMessage = Boolean(recordedAudioBlob);

      const messageType: MessagePayloadType = isAudioMessage
        ? 'audio'
        : hasVideos && !hasImages
          ? 'video'
          : hasImages && !hasVideos
            ? 'image'
            : isMediaMessage
              ? 'video'
              : isDocMessage
                ? 'file'
                : 'text';

      const parentRef = doc(db, 'flutter_chat_messages', chatId);
      const messagesRef = inquiryMessageId
        ? collection(
            db,
            'flutter_chat_messages',
            chatId,
            'messages',
            inquiryMessageId,
            'inquiry_message',
          )
        : collection(db, 'flutter_chat_messages', chatId, 'messages');
      const newMsgRef = doc(messagesRef);

      const serverTime = serverTimestamp();

      const unreadField = `unread_count_${clientUid}`;
      const trainerName = currentUser
        ? `${currentUser.name || (currentUser as any).first_name || ''} ${currentUser.surname || (currentUser as any).last_name || ''}`.trim() ||
          auth.currentUser?.displayName ||
          'Trainer'
        : auth.currentUser?.displayName || 'Trainer';

      const trainerAvatar =
        currentUser?.avatarUrl ||
        (currentUser as any)?.profile_picture_url ||
        (currentUser as any)?.profilePicture ||
        auth.currentUser?.photoURL ||
        '';

      const clientName = clientInfo
        ? `${clientInfo.name || ''} ${clientInfo.surname || ''}`.trim()
        : currentInquiry?.sender_name || 'Client';

      const clientAvatar =
        clientInfo?.avatarUrl ||
        (clientInfo as any)?.profile_picture ||
        currentInquiry?.sender_avatar ||
        '';

      const newMsgData: Record<string, any> = {
        sender_uid: trainerActualUid,
        receive_uid: clientUid,
        sender_role: 'trainer',
        receive_role: 'client',
        sender_name: trainerName,
        receive_name: clientName,
        sender_avatar: trainerAvatar,
        receive_avatar: clientAvatar,
        sender_gender: '1',
        receive_gender: '1',
        body: textToSend,
        message_type: messageType,
        send_time: serverTime,
        is_read: false,
        read_time: null,
      };

      if (isMediaMessage) {
        newMsgData.media_url = mediaAttachments[0].url;
        newMsgData.attachments = mediaAttachments;
        if (hasVideos) {
          newMsgData.file_name = mediaAttachments[0].file_name;
          newMsgData.file_size = mediaAttachments[0].size_bytes;
          newMsgData.size_label = mediaAttachments[0].size_label;
        }
      }

      if (isDocMessage) {
        newMsgData.media_url = docAttachments[0].url;
        newMsgData.file_name = docAttachments[0].file_name;
        newMsgData.file_size = docAttachments[0].size_bytes;
        newMsgData.size_label = docAttachments[0].size_label;
        newMsgData.attachments = docAttachments;
      }

      if (inquiryMessageId) {
        newMsgData.inquiry_id = inquiryMessageId;
      }

      const extractedLinks = extractAllUrls(textToSend);
      if (extractedLinks.length > 0) {
        const previews = extractedLinks.map((url) => {
          const linkMeta = getInitialLinkMetadata(url, textToSend);
          return {
            url: linkMeta.url,
            title: linkMeta.title,
            description: linkMeta.description,
            domain: linkMeta.domain,
            display_url: linkMeta.displayUrl,
            type: linkMeta.type,
          };
        });
        newMsgData.link_previews = previews;
        newMsgData.link_preview = previews[0];
      }

      // 1. Instantly update UI before any network awaits
      const optimisticMsg = {
        id: newMsgRef.id,
        ...newMsgData,
        send_time: new Date().toISOString(),
        send_uid: trainerActualUid,
        isOptimistic: true,
      } as unknown as ChatMessage;

      setMessages((prev) => {
        if (prev.some((m) => m.id === optimisticMsg.id)) return prev;
        return [...prev, optimisticMsg];
      });

      // 2. Now perform network operations
      let uploadedAudioUrl: string | null = null;
      if (recordedAudioBlob) {
        uploadedAudioUrl = await uploadChatAudio(recordedAudioBlob, chatId, trainerActualUid);
        newMsgData.audio_url = uploadedAudioUrl;
        newMsgData.audio_duration_seconds = recordedDuration;
      }

      const batch = writeBatch(db);

      if (inquiryMessageId) {
        // Inquiry reply: write exclusively to inquiry_message subcollection
        batch.set(newMsgRef, newMsgData);

        // Update inquiry document status to 'replied' if not resolved
        const inqRef = doc(db, 'flutter_chat_messages', chatId, 'messages', inquiryMessageId);
        if (!isInquiryResolved) {
          batch.update(inqRef, {
            status: 'replied',
            status_updated_at: serverTime,
            status_updated_by: trainerActualUid,
          });
        }
        // In accordance with INQUIRY_DATA_FLOW.md, reply writes never touch parent conversation
      } else {
        // Direct conversation message: updates parent conversation metadata
        const parentSnap = await getDoc(parentRef);
        if (!parentSnap.exists()) {
          batch.set(parentRef, {
            created_time: serverTime,
            participants: [trainerActualUid, clientUid],
            trainer_ids: [trainerActualUid],
          });
        }

        batch.set(newMsgRef, newMsgData);

        const parentUpdate: Record<string, unknown> = {
          [unreadField]: ((parentSnap.data()?.[unreadField] as number) || 0) + 1,
        };

        let snippet = textToSend;
        if (isAudioMessage) {
          snippet = `🎤 Voice note${textToSend ? ': ' + textToSend : ''}`;
        } else if (isMediaMessage) {
          const videoCount = mediaAttachments.filter((m) => m.type === 'video').length;
          const imageCount = mediaAttachments.filter((m) => m.type === 'image').length;
          let mediaLabel = 'Photo';
          if (videoCount > 0 && imageCount === 0) {
            mediaLabel = `🎥 Video${videoCount > 1 ? ` (${videoCount})` : ''}`;
          } else if (imageCount > 0 && videoCount === 0) {
            mediaLabel = `📷 Photo${imageCount > 1 ? ` (${imageCount})` : ''}`;
          } else {
            mediaLabel = `🎬 Media (${mediaAttachments.length})`;
          }
          snippet = `${mediaLabel}${textToSend ? ': ' + textToSend : ''}`;
        } else if (isDocMessage) {
          snippet = `📄 ${docAttachments[0].file_name || 'Document'}${docAttachments.length > 1 ? ` (+${docAttachments.length - 1})` : ''}${textToSend ? ': ' + textToSend : ''}`;
        }

        parentUpdate.last_message = {
          ...newMsgData,
          body: snippet,
          send_time: serverTime,
        };
        parentUpdate.last_message_time = serverTime;

        batch.set(parentRef, parentUpdate, { merge: true });
      }

      await batch.commit();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleOpenDocumentTemplate = (docType: string) => {
    window.open(
      `/onboarding-trainer/dashboard/profile?tab=packages&view=template&type=${docType}`,
      '_blank',
    );
  };

  const handleConfirmSendOffer = async (offerData: {
    pkg: TrainerPackage;
    startDate: string;
    whatsIncluded: string[];
  }) => {
    if (!chatId || !trainerActualUid || !clientUid) return;

    setIsSendingOffer(true);
    try {
      const parentRef = doc(db, 'flutter_chat_messages', chatId);
      const messagesRef = collection(db, 'flutter_chat_messages', chatId, 'messages');
      const newMsgRef = doc(messagesRef);
      const serverTime = serverTimestamp();
      const unreadField = `unread_count_${clientUid}`;

      const trainerName = currentUser
        ? `${currentUser.name || (currentUser as any).first_name || ''} ${currentUser.surname || (currentUser as any).last_name || ''}`.trim() ||
          auth.currentUser?.displayName ||
          'Trainer'
        : auth.currentUser?.displayName || 'Trainer';

      const trainerAvatar =
        currentUser?.avatarUrl ||
        (currentUser as any)?.profile_picture_url ||
        (currentUser as any)?.profilePicture ||
        auth.currentUser?.photoURL ||
        '';

      const clientName = clientInfo
        ? `${clientInfo.name || ''} ${clientInfo.surname || ''}`.trim()
        : currentInquiry?.sender_name || 'Client';

      const clientAvatar =
        clientInfo?.avatarUrl ||
        (clientInfo as any)?.profile_picture ||
        currentInquiry?.sender_avatar ||
        '';

      const newMsgData: Record<string, any> = {
        sender_uid: trainerActualUid,
        receive_uid: clientUid,
        sender_role: 'trainer',
        receive_role: 'client',
        sender_name: trainerName,
        receive_name: clientName,
        sender_avatar: trainerAvatar,
        receive_avatar: clientAvatar,
        body: `Package Offer: ${offerData.pkg.name}`,
        message_type: 'package_offer',
        package_id: offerData.pkg.id,
        package_name: offerData.pkg.name,
        price: offerData.pkg.price,
        currency: offerData.pkg.currency || 'GBP',
        currency_symbol:
          offerData.pkg.currencySymbol || (offerData.pkg.currency === 'GBP' ? '£' : '$'),
        duration: offerData.pkg.duration || '4 weeks',
        start_date: offerData.startDate,
        offer_status: 'awaiting_response',
        whats_included: offerData.whatsIncluded,
        attached_documents: [
          { id: 'checkin_policy', title: 'Check-in policy', type: 'checkin_policy' },
          { id: 'contractual_terms', title: 'Contractual terms', type: 'contractual_terms' },
          {
            id: 'cancellation_policy',
            title: 'Cancellation and unused policy',
            type: 'cancellation_policy',
          },
        ],
        send_time: serverTime,
        is_read: false,
        read_time: null,
      };

      // Optimistic UI update
      const optimisticMsg = {
        id: newMsgRef.id,
        ...newMsgData,
        send_time: new Date().toISOString(),
        send_uid: trainerActualUid,
        isOptimistic: true,
      } as unknown as ChatMessage;

      setMessages((prev) => {
        if (prev.some((m) => m.id === optimisticMsg.id)) return prev;
        return [...prev, optimisticMsg];
      });

      const batch = writeBatch(db);
      const parentSnap = await getDoc(parentRef);
      if (!parentSnap.exists()) {
        batch.set(parentRef, {
          created_time: serverTime,
          participants: [trainerActualUid, clientUid],
          trainer_ids: [trainerActualUid],
        });
      }

      batch.set(newMsgRef, newMsgData);

      const parentUpdate: Record<string, unknown> = {
        [unreadField]: ((parentSnap.data()?.[unreadField] as number) || 0) + 1,
        last_message: {
          ...newMsgData,
          body: `🎁 Offer sent: ${offerData.pkg.name}`,
          send_time: serverTime,
        },
        last_message_time: serverTime,
      };

      batch.set(parentRef, parentUpdate, { merge: true });
      await batch.commit();

      setIsSendOfferModalOpen(false);
      toast.success('Offer sent successfully!');
    } catch (err) {
      console.error('Error sending package offer:', err);
      toast.error('Failed to send offer. Please try again.');
    } finally {
      setIsSendingOffer(false);
    }
  };

  const handleConfirmSendInvitation = async (invitationData: {
    callTitle: string;
    scheduleSummary: string;
    scheduleDays?: ScheduleDayItem[];
    timezone: string;
  }) => {
    if (!chatId || !trainerActualUid || !clientUid) return;

    setIsSendingInvitation(true);
    try {
      const parentRef = doc(db, 'flutter_chat_messages', chatId);
      const messagesRef = collection(db, 'flutter_chat_messages', chatId, 'messages');
      const newMsgRef = doc(messagesRef);
      const serverTime = serverTimestamp();
      const unreadField = `unread_count_${clientUid}`;

      const trainerRecord = currentUser as unknown as Record<string, unknown> | null;
      const clientRecord = clientInfo as unknown as Record<string, unknown> | null;

      const trainerName = currentUser
        ? `${currentUser.name || (trainerRecord?.first_name as string) || ''} ${currentUser.surname || (trainerRecord?.last_name as string) || ''}`.trim() ||
          auth.currentUser?.displayName ||
          'Trainer'
        : auth.currentUser?.displayName || 'Trainer';

      const trainerAvatar =
        currentUser?.avatarUrl ||
        (trainerRecord?.profile_picture_url as string) ||
        (trainerRecord?.profilePicture as string) ||
        auth.currentUser?.photoURL ||
        '';

      const clientName = clientInfo
        ? `${clientInfo.name || ''} ${clientInfo.surname || ''}`.trim()
        : currentInquiry?.sender_name || 'Client';

      const clientAvatar =
        clientInfo?.avatarUrl ||
        (clientRecord?.profile_picture as string) ||
        currentInquiry?.sender_avatar ||
        '';

      const newMsgData: Record<string, unknown> = {
        sender_uid: trainerActualUid,
        receive_uid: clientUid,
        sender_role: 'trainer',
        receive_role: 'client',
        sender_name: trainerName,
        receive_name: clientName,
        sender_avatar: trainerAvatar,
        receive_avatar: clientAvatar,
        body: `Free Call Invitation: ${invitationData.callTitle}`,
        message_type: 'free_call_invitation',
        call_title: invitationData.callTitle,
        call_duration: '15-min',
        call_status: 'awaiting_response',
        trainer_schedule_summary: invitationData.scheduleSummary,
        trainer_timezone: invitationData.timezone,
        ...(invitationData.scheduleDays && invitationData.scheduleDays.length > 0
          ? { trainer_schedule_days: invitationData.scheduleDays }
          : {}),
        send_time: serverTime,
        is_read: false,
        read_time: null,
      };

      // Optimistic UI update
      const optimisticMsg = {
        id: newMsgRef.id,
        ...newMsgData,
        send_time: new Date().toISOString(),
        send_uid: trainerActualUid,
        isOptimistic: true,
      } as unknown as ChatMessage;

      setMessages((prev) => {
        if (prev.some((m) => m.id === optimisticMsg.id)) return prev;
        return [...prev, optimisticMsg];
      });

      const batch = writeBatch(db);
      const parentSnap = await getDoc(parentRef);
      if (!parentSnap.exists()) {
        batch.set(parentRef, {
          created_time: serverTime,
          participants: [trainerActualUid, clientUid],
          trainer_ids: [trainerActualUid],
        });
      }

      batch.set(newMsgRef, newMsgData);

      const parentUpdate: Record<string, unknown> = {
        [unreadField]: ((parentSnap.data()?.[unreadField] as number) || 0) + 1,
        last_message: {
          ...newMsgData,
          body: `📞 Free call invitation: ${invitationData.callTitle}`,
          send_time: serverTime,
        },
        last_message_time: serverTime,
      };

      batch.set(parentRef, parentUpdate, { merge: true });
      await batch.commit();

      setIsInviteCallModalOpen(false);
      toast.success('Invitation sent successfully!');
    } catch (err) {
      console.error('Error sending free call invitation:', err);
      toast.error('Failed to send invitation. Please try again.');
    } finally {
      setIsSendingInvitation(false);
    }
  };

  const handleMediaFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (isChatReadOnly || !files.length || !chatId || !trainerActualUid) return;

    if (mediaFileInputRef.current) {
      mediaFileInputRef.current.value = '';
    }

    // Mutual exclusivity: cancel/clear voice note if active
    if (audioRecorder.isRecording || audioRecorder.audioBlob) {
      audioRecorder.cancelRecording();
      audioRecorder.clearAudio();
    }

    const availableSlots = MAX_MEDIA_COUNT - attachedMedia.length;
    if (availableSlots <= 0) {
      setMediaLimitWarning(`Maximum of ${MAX_MEDIA_COUNT} photos/videos allowed per message.`);
      return;
    }

    if (files.length > availableSlots) {
      setMediaLimitWarning(
        `Limit exceeded: Only ${availableSlots} of ${files.length} selected items were attached (max ${MAX_MEDIA_COUNT}).`,
      );
    } else {
      setMediaLimitWarning(null);
    }

    const filesToAdd = files.slice(0, availableSlots);
    const validItems: AttachedMediaItem[] = [];

    for (const file of filesToAdd) {
      const isVideo =
        ALLOWED_VIDEO_TYPES.includes(file.type) || isPureVideoExtension(file.name, file.type);

      const isImage =
        !isVideo &&
        (ALLOWED_IMAGE_TYPES.includes(file.type) || isPureImageExtension(file.name, file.type));

      if (!isVideo && !isImage) {
        toast.error(`"${file.name}" is not a supported image or video format.`);
        continue;
      }

      if (isVideo) {
        if (file.size > MAX_VIDEO_SIZE_BYTES) {
          toast.error(`"${file.name}" exceeds the 50 MB limit for videos.`);
          continue;
        }

        validItems.push({
          id: crypto.randomUUID(),
          file,
          mediaType: 'video',
          previewUrl: URL.createObjectURL(file),
          isUploading: true,
        });
      } else {
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
          toast.error(`"${file.name}" exceeds the 3 MB limit for images.`);
          continue;
        }

        validItems.push({
          id: crypto.randomUUID(),
          file,
          mediaType: 'image',
          previewUrl: URL.createObjectURL(file),
          isUploading: true,
        });
      }
    }

    if (!validItems.length) return;

    setAttachedMedia((prev) => [...prev, ...validItems]);

    // Instant upload to Firebase Storage
    for (const item of validItems) {
      const uploadPromise =
        item.mediaType === 'video'
          ? uploadChatVideo(item.file, chatId, trainerActualUid)
          : uploadChatImage(item.file, chatId, trainerActualUid);

      uploadPromise
        .then((res) => {
          setAttachedMedia((prev) =>
            prev.map((m) =>
              m.id === item.id
                ? {
                    ...m,
                    storageUrl: res.url,
                    storagePath: res.filePath,
                    isUploading: false,
                  }
                : m,
            ),
          );
        })
        .catch((err) => {
          console.error(`Error uploading chat ${item.mediaType}:`, err);
          toast.error(`Failed to upload ${item.file.name}`);
          setAttachedMedia((prev) =>
            prev.map((m) =>
              m.id === item.id ? { ...m, isUploading: false, uploadError: true } : m,
            ),
          );
        });
    }
  };

  const handleRemoveAttachedMedia = (item: AttachedMediaItem) => {
    const targetPath = item.storagePath || item.storageUrl;
    if (targetPath) {
      deleteChatMedia(targetPath);
    }
    URL.revokeObjectURL(item.previewUrl);
    setAttachedMedia((prev) => {
      const remaining = prev.filter((m) => m.id !== item.id);
      if (remaining.length < MAX_MEDIA_COUNT) {
        setMediaLimitWarning(null);
      }
      return remaining;
    });
  };

  const handleDocFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (isChatReadOnly || !files.length || !chatId || !trainerActualUid) return;

    if (docFileInputRef.current) {
      docFileInputRef.current.value = '';
    }

    if (audioRecorder.isRecording || audioRecorder.audioBlob) {
      audioRecorder.cancelRecording();
      audioRecorder.clearAudio();
    }

    const availableSlots = MAX_DOCS_COUNT - attachedDocs.length;
    if (availableSlots <= 0) {
      setDocLimitWarning(`Maximum of ${MAX_DOCS_COUNT} files allowed per message.`);
      return;
    }

    if (files.length > availableSlots) {
      setDocLimitWarning(
        `Limit exceeded: Only ${availableSlots} of ${files.length} selected files were attached (max ${MAX_DOCS_COUNT}).`,
      );
    } else {
      setDocLimitWarning(null);
    }

    const filesToAdd = files.slice(0, availableSlots);
    const validItems: AttachedDocItem[] = [];

    for (const file of filesToAdd) {
      const isAllowedDoc =
        ALLOWED_DOCUMENT_TYPES.includes(file.type) ||
        /\.(pdf|docx?|xlsx?|pptx?|txt|csv|rtf)$/i.test(file.name);

      if (!isAllowedDoc) {
        toast.error(`"${file.name}" format is not supported.`);
        continue;
      }

      if (file.size > MAX_DOC_SIZE_BYTES) {
        toast.error(`"${file.name}" exceeds the 3 MB limit.`);
        continue;
      }

      validItems.push({
        id: crypto.randomUUID(),
        file,
        sizeBytes: file.size,
        isUploading: true,
      });
    }

    if (!validItems.length) return;

    setAttachedDocs((prev) => [...prev, ...validItems]);

    // Instant upload to Firebase Storage
    for (const item of validItems) {
      uploadChatFile(item.file, chatId, trainerActualUid)
        .then((res) => {
          setAttachedDocs((prev) =>
            prev.map((docItem) =>
              docItem.id === item.id
                ? {
                    ...docItem,
                    storageUrl: res.url,
                    storagePath: res.filePath,
                    isUploading: false,
                  }
                : docItem,
            ),
          );
        })
        .catch((err) => {
          console.error('Error uploading chat doc:', err);
          toast.error(`Failed to upload ${item.file.name}`);
          setAttachedDocs((prev) =>
            prev.map((docItem) =>
              docItem.id === item.id
                ? { ...docItem, isUploading: false, uploadError: true }
                : docItem,
            ),
          );
        });
    }
  };

  const handleRemoveAttachedDoc = (item: AttachedDocItem) => {
    const targetPath = item.storagePath || item.storageUrl;
    if (targetPath) {
      deleteChatMedia(targetPath);
    }
    setAttachedDocs((prev) => {
      const remaining = prev.filter((d) => d.id !== item.id);
      if (remaining.length < MAX_DOCS_COUNT) {
        setDocLimitWarning(null);
      }
      return remaining;
    });
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji;
    if (textareaRef.current) {
      const { selectionStart, selectionEnd } = textareaRef.current;
      const text = inputText;
      const newText = text.substring(0, selectionStart) + emoji + text.substring(selectionEnd);
      setInputText(newText);
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const newPos = selectionStart + emoji.length;
          textareaRef.current.setSelectionRange(newPos, newPos);
        }
      }, 0);
    } else {
      setInputText((prev) => prev + emoji);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isEnter =
      e.key === 'Enter' ||
      e.code === 'Enter' ||
      e.code === 'NumpadEnter' ||
      e.keyCode === 13 ||
      e.which === 13;

    // Shift + Enter on either main Enter or Keypad Enter -> new line
    if (isEnter && e.shiftKey) {
      e.preventDefault();
      const target = e.currentTarget;
      const start = target.selectionStart;
      const end = target.selectionEnd;
      const currentVal = target.value;
      const nextVal = currentVal.substring(0, start) + '\n' + currentVal.substring(end);
      setInputText(nextVal);
      setTimeout(() => {
        target.selectionStart = target.selectionEnd = start + 1;
      }, 0);
      return;
    }

    // On mobile devices, Enter creates a new line (send via send button)
    const isMobile =
      typeof window !== 'undefined' &&
      (window.innerWidth <= 768 || /iphone|ipad|ipod|android/i.test(navigator.userAgent));

    if (isMobile) {
      return;
    }

    // Regular Enter (without Shift/Alt/Ctrl/Cmd) -> Send message
    if (isEnter && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    setDropdownOpen(false);
    if (e.key === 'profile') {
      if (clientUid) {
        navigate(`/onboarding-trainer/dashboard/clients/${clientUid}/client-profile`);
      }
    } else if (e.key === 'search') {
      setIsSearchActive(true);
      setSearchQuery('');
      setCurrentMatchIndex(0);
    } else if (e.key === 'media_files_links') {
      if (chatId) {
        const targetUrl = inquiryMessageId
          ? `/onboarding-trainer/dashboard/messages/${chatId}/media?inquiryId=${inquiryMessageId}`
          : `/onboarding-trainer/dashboard/messages/${chatId}/media`;
        navigate(targetUrl);
      } else {
        setActiveView('media_files_links');
        setInitialMLFTab('media');
      }
    } else if (e.key === 'report') {
      setShowReportView(true);
    }
  };

  const moreMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <ViewClientProfileIcon className="dropdown-menu-icon" />,
      label: 'View client profile',
    },
    {
      key: 'search',
      icon: <SearchInConversationIcon className="dropdown-menu-icon" />,
      label: 'Search in conversation',
    },
    {
      key: 'media_files_links',
      icon: <ViewMediaLinksFilesIcon className="dropdown-menu-icon" />,
      label: 'View Media, links & files',
    },
    {
      key: 'report',
      icon: <ReportAnIssueIcon className="dropdown-menu-icon" />,
      label: 'Report an issue',
    },
  ];

  const getInitials = (fullName: string) => {
    if (!fullName) return 'C';
    const parts = fullName.trim().split(' ');
    if (parts.length > 1) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return fullName.substring(0, 2).toUpperCase();
  };

  const isSupportChat = useMemo(() => {
    return (
      Boolean(chatId && chatId.startsWith('support_')) ||
      chatParentData?.is_support === true ||
      chatParentData?.type === 'support' ||
      messages.some((m) => m.message_type === 'support' || m.is_support)
    );
  }, [chatId, chatParentData, messages]);

  const clientFullName = isSupportChat
    ? 'miniPT Support'
    : clientInfo
      ? `${clientInfo.name} ${clientInfo.surname}`.trim()
      : flutterUserData?.name
        ? `${flutterUserData.name} ${flutterUserData.surname || ''}`.trim()
        : currentInquiry?.sender_name ||
          (chatParentData?.client_name as string) ||
          (chatParentData?.other_name as string) ||
          'Client';

  const clientAvatarUrl = isSupportChat
    ? ''
    : clientInfo?.avatarUrl ||
      flutterUserData?.avatar_url ||
      flutterUserData?.avatar ||
      flutterUserData?.profile_picture ||
      currentInquiry?.sender_avatar ||
      (chatParentData?.client_avatar as string) ||
      '';

  const clientEmailDisplay = isSupportChat
    ? (chatParentData?.support_email as string) || 'support@minipt.co.uk'
    : clientInfo?.email?.trim() ||
      flutterUserData?.email?.trim() ||
      (currentInquiry as any)?.sender_email ||
      (currentInquiry as any)?.client_email ||
      (chatParentData?.client_email as string) ||
      '';

  const groupMessagesByDate = () => {
    const groups: { key: string; dateLabel: string; messages: ChatMessage[] }[] = [];
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();

    displayMessages.forEach((msg) => {
      const msgDate = parseFirestoreDate(msg.send_time);
      const dateString = msgDate.toDateString();

      let dateLabel = dateString;
      if (dateString === today) dateLabel = 'TODAY';
      else if (dateString === yesterday) dateLabel = 'YESTERDAY';
      else {
        dateLabel = msgDate.toLocaleDateString([], {
          month: 'short',
          day: 'numeric',
          year: msgDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
        });
      }

      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.dateLabel === dateLabel) {
        lastGroup.messages.push(msg);
      } else {
        groups.push({
          key: `date_group_${dateString}`,
          dateLabel,
          messages: [msg],
        });
      }
    });

    return groups;
  };

  return (
    <div className="dashboard-chat-details">
      {/* Sticky Header */}
      {inquiryMessageId && !isSupportChat ? (
        <div className="chat-header inquiry-header">
          <div className="header-left">
            <button
              className="back-button"
              onClick={() => {
                navigate('/onboarding-trainer/dashboard/messages?tab=inquiries');
              }}
              aria-label="Back to inquiries"
            >
              <ChatBackArrowIcon />
            </button>
            <div className="client-info">
              <div
                className="client-avatar"
                style={{
                  backgroundImage: clientAvatarUrl ? `url(${clientAvatarUrl})` : 'none',
                }}
              >
                {!clientAvatarUrl && getInitials(clientFullName)}
              </div>
              <div className="client-name-email">
                <h2>{clientFullName}</h2>
                {clientEmailDisplay && <span>{clientEmailDisplay}</span>}
              </div>
            </div>
          </div>
          <div className="header-right">
            <button
              className={`mark-resolved-btn ${isInquiryResolved ? 'is-resolved' : ''}`}
              onClick={handleToggleResolveInquiry}
            >
              <span>Mark as resolved</span>
              {isInquiryResolved ? (
                <ResolvedCheckboxCheckedIcon className="resolved-checkbox-icon" />
              ) : (
                <ResolvedCheckboxUncheckedIcon className="resolved-checkbox-icon" />
              )}
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`chat-header ${(!inquiryMessageId || isSupportChat) && isSearchActive ? 'hidden-on-mobile-search' : ''}`}
        >
          <div className="header-left">
            <button
              className="back-button"
              onClick={() => {
                if (isSupportChat || inquiryMessageId || searchParams.get('tab') === 'inquiries') {
                  navigate('/onboarding-trainer/dashboard/messages?tab=inquiries');
                } else {
                  navigate('/onboarding-trainer/dashboard/messages');
                }
              }}
            >
              <ChatBackArrowIcon />
            </button>
            <div className="client-info">
              <div
                className={`client-avatar ${isSupportChat ? 'is-support-avatar' : ''}`}
                style={{
                  backgroundImage: clientAvatarUrl ? `url(${clientAvatarUrl})` : 'none',
                }}
              >
                {isSupportChat ? (
                  <SupportHeadsetIcon className="support-headset-icon" />
                ) : (
                  !clientAvatarUrl && getInitials(clientFullName)
                )}
              </div>
              <div className="client-name-email">
                <h2>{clientFullName}</h2>
                {clientEmailDisplay && <span>{clientEmailDisplay}</span>}
              </div>
            </div>
          </div>
          <div className="header-right">
            <Dropdown
              menu={{ items: moreMenuItems, onClick: handleMenuClick }}
              trigger={['click']}
              placement="bottomRight"
              classNames={{ root: 'chat-header-dropdown-menu' }}
              open={dropdownOpen}
              onOpenChange={setDropdownOpen}
            >
              <button className="more-actions-button" aria-label="More actions">
                {dropdownOpen ? (
                  <MoreMenuCloseIcon className="more-menu-btn-icon" />
                ) : (
                  <MoreMenuIcon className="more-menu-btn-icon" />
                )}
              </button>
            </Dropdown>
          </div>
        </div>
      )}

      {/* Search Bar (when activated via dropdown menu) */}
      {(!inquiryMessageId || isSupportChat) && isSearchActive && (
        <div className="search-bar-container">
          <Input
            ref={searchInputRef}
            prefix={
              isSearchingFullHistory || isFetchingContext ? (
                <LoadingOutlined spin style={{ color: '#FFA100', fontSize: 16 }} />
              ) : (
                <SearchOutlined />
              )
            }
            placeholder="Search in conversation..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentMatchIndex(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (searchMatches.length > 0) {
                  if (e.shiftKey) {
                    // Shift+Enter goes to latest match (newer)
                    setCurrentMatchIndex((prev) =>
                      prev > 0 ? prev - 1 : searchMatches.length - 1,
                    );
                  } else {
                    // Enter goes to previous match (older)
                    setCurrentMatchIndex((prev) =>
                      prev < searchMatches.length - 1 ? prev + 1 : 0,
                    );
                  }
                }
              } else if (e.key === 'Escape') {
                setIsSearchActive(false);
                setSearchQuery('');
                setCurrentMatchIndex(0);
              }
            }}
            autoFocus
          />
          {debouncedSearchQuery.trim() && (
            <div className="search-controls">
              <span className="match-count">
                {isSearchingFullHistory
                  ? 'Searching...'
                  : isFetchingContext
                    ? 'Loading...'
                    : searchMatches.length > 0
                      ? `${currentMatchIndex + 1} of ${searchMatches.length}`
                      : '0 matches'}
              </span>
              <Button
                type="text"
                className="search-nav-btn"
                icon={<LeftOutlined />}
                disabled={
                  searchMatches.length === 0 ||
                  currentMatchIndex >= searchMatches.length - 1 ||
                  isFetchingContext
                }
                onClick={() =>
                  setCurrentMatchIndex((prev) => Math.min(searchMatches.length - 1, prev + 1))
                }
                title="Previous message (Enter)"
              />
              <Button
                type="text"
                className="search-nav-btn"
                icon={<RightOutlined />}
                disabled={searchMatches.length === 0 || currentMatchIndex <= 0 || isFetchingContext}
                onClick={() => setCurrentMatchIndex((prev) => Math.max(0, prev - 1))}
                title="Latest message (Shift+Enter)"
              />
            </div>
          )}
          <Button
            type="text"
            className="search-close-btn"
            icon={<CloseOutlined />}
            onClick={() => {
              setIsSearchActive(false);
              setSearchQuery('');
              setCurrentMatchIndex(0);
            }}
            title="Close search (Esc)"
          />
        </div>
      )}

      {/* Floating Top Loader Badge */}
      <div className={`chat-floating-load-earlier ${loadingEarlier ? 'visible' : ''}`}>
        <div className="load-more-loader-pill">
          <LoadingOutlined spin className="loader-spin-icon" />
          <span className="loader-text">Loading older messages...</span>
        </div>
      </div>

      {/* Messages Stream */}
      <div className="chat-messages-container" ref={messagesContainerRef} onScroll={handleScroll}>
        {loading ? (
          <div className="chat-loading-skeleton-container">
            <div className="skeleton-bubble-row received">
              <div className="skeleton-avatar" />
              <div className="skeleton-bubble-content skeleton-w-55" />
            </div>
            <div className="skeleton-bubble-row sent">
              <div className="skeleton-bubble-content skeleton-w-40" />
            </div>
            <div className="skeleton-bubble-row received">
              <div className="skeleton-avatar" />
              <div className="skeleton-bubble-content skeleton-w-70" />
            </div>
            <div className="skeleton-bubble-row sent">
              <div className="skeleton-bubble-content skeleton-w-45" />
            </div>
            <div className="skeleton-bubble-row received">
              <div className="skeleton-avatar" />
              <div className="skeleton-bubble-content skeleton-w-60" />
            </div>
          </div>
        ) : (
          groupMessagesByDate().map((group) => (
            <React.Fragment key={group.key}>
              <div className="date-divider">
                <span>{group.dateLabel}</span>
              </div>
              {group.messages.map((msg) => {
                const isSupportMessage = msg.message_type === 'support' || msg.is_support === true;
                const isCheckin = msg.message_type === 'checkin';
                const isPackageRequest = msg.message_type === 'package_request';
                const isPackageOffer = msg.message_type === 'package_offer';
                const isFreeCallInvitation = msg.message_type === 'free_call_invitation';
                const isPackageActive =
                  msg.message_type === 'package_active' || msg.message_type === 'package_activated';
                const isInquiry =
                  !isSupportMessage &&
                  !isCheckin &&
                  !isPackageRequest &&
                  !isPackageOffer &&
                  !isFreeCallInvitation &&
                  !isPackageActive &&
                  (msg.message_type === 'inquiry' ||
                    (!isSupportChat && msg.id === inquiryMessageId));

                const msgSenderUid = msg.sender_uid || msg.send_uid;
                const isSentByTrainer =
                  msg.sender_role === 'trainer' ||
                  (Boolean(msgSenderUid) &&
                    Boolean(trainerActualUid) &&
                    msgSenderUid === trainerActualUid) ||
                  (isSupportChat && isSupportMessage);
                const timeStr = msg.send_time
                  ? parseFirestoreDate(msg.send_time).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '';
                const isCurrentMatch =
                  isSearchActive && searchMatches[currentMatchIndex] === msg.id;

                // Render Package Request Bubble (Figma Messages • Individual Messages-2)
                if (isPackageRequest) {
                  return (
                    <div
                      id={`msg-${msg.id}`}
                      key={msg.id}
                      data-message-id={msg.id}
                      data-unread={!isSentByTrainer && !msg.is_read}
                      className={`message-bubble-wrapper ${isSentByTrainer ? 'sent' : 'received'} ${isCurrentMatch ? 'search-match-active' : ''}`}
                      style={{ maxWidth: '85%' }}
                    >
                      {!isSentByTrainer && (
                        <div
                          className="message-sender-avatar"
                          style={
                            clientAvatarUrl ? { backgroundImage: `url(${clientAvatarUrl})` } : {}
                          }
                        >
                          {!clientAvatarUrl && getInitials(clientFullName)}
                        </div>
                      )}
                      <div className="message-content-box" style={{ width: '100%' }}>
                        <PackageRequestBubble
                          message={msg}
                          onSendOffer={(m) => {
                            setSelectedRequestMessage(m);
                            setIsSendOfferModalOpen(true);
                          }}
                          onInviteFreeCall={(m) => {
                            setSelectedRequestMessage(m);
                            setIsInviteCallModalOpen(true);
                          }}
                        />
                        <div className="message-meta">
                          <MessageReadStatus
                            isRead={msg.is_read}
                            isSentByTrainer={isSentByTrainer}
                          />
                          <span>{timeStr}</span>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Render Package Offer Bubble (Figma Messages • Individual Messages-5 & 6)
                if (isPackageOffer) {
                  const hasSeparateActiveMsg = messages.some(
                    (m) =>
                      m.message_type === 'package_active' || m.message_type === 'package_activated',
                  );
                  const isOfferConfirmed =
                    msg.offer_status === 'confirmed' ||
                    msg.offer_status === 'accepted' ||
                    msg.offer_status === 'active';

                  return (
                    <React.Fragment key={msg.id}>
                      <div
                        id={`msg-${msg.id}`}
                        data-message-id={msg.id}
                        data-unread={!isSentByTrainer && !msg.is_read}
                        className={`message-bubble-wrapper ${isSentByTrainer ? 'sent' : 'received'} ${isCurrentMatch ? 'search-match-active' : ''}`}
                        style={{ maxWidth: '85%' }}
                      >
                        <div className="message-content-box" style={{ width: '100%' }}>
                          <PackageOfferBubble
                            message={msg}
                            onOpenDocument={handleOpenDocumentTemplate}
                          />
                          <div className="message-meta">
                            <MessageReadStatus
                              isRead={msg.is_read}
                              isSentByTrainer={isSentByTrainer}
                            />
                            <span>{timeStr}</span>
                          </div>
                        </div>
                      </div>
                      {/* If offer is confirmed and there is no separate package_active message, show active banner directly below offer */}
                      {isOfferConfirmed && !hasSeparateActiveMsg && (
                        <div
                          key={`active-banner-${msg.id}`}
                          className="package-active-message-wrapper"
                          style={{ width: '100%' }}
                        >
                          <PackageActiveBanner
                            message={msg}
                            clientName={clientFullName}
                            packageName={msg.package_name}
                          />
                        </div>
                      )}
                    </React.Fragment>
                  );
                }

                // Render Package Active Banner (Figma Messages • Individual Messages-6)
                if (isPackageActive) {
                  return (
                    <div
                      id={`msg-${msg.id}`}
                      key={msg.id}
                      data-message-id={msg.id}
                      className={`package-active-message-wrapper ${isCurrentMatch ? 'search-match-active' : ''}`}
                      style={{ width: '100%' }}
                    >
                      <PackageActiveBanner
                        message={msg}
                        clientName={clientFullName}
                        packageName={msg.package_name}
                      />
                    </div>
                  );
                }

                // Render Free Call Invitation Bubble (Figma Messages • Individual Messages-3 & 4)
                if (isFreeCallInvitation) {
                  return (
                    <div
                      id={`msg-${msg.id}`}
                      key={msg.id}
                      data-message-id={msg.id}
                      data-unread={!isSentByTrainer && !msg.is_read}
                      className={`message-bubble-wrapper ${isSentByTrainer ? 'sent' : 'received'} ${isCurrentMatch ? 'search-match-active' : ''}`}
                      style={{ maxWidth: '85%' }}
                    >
                      <div className="message-content-box" style={{ width: '100%' }}>
                        <FreeCallInvitationBubble message={msg} clientName={clientFullName} />
                        <div className="message-meta">
                          <MessageReadStatus
                            isRead={msg.is_read}
                            isSentByTrainer={isSentByTrainer}
                          />
                          <span>{timeStr}</span>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Render Support Card Bubble (Figma Dashboard-2)
                if (isSupportMessage) {
                  const supportSubject = msg.subject || msg.inquirySubject || 'Support Request';
                  const supportTopic =
                    msg.topic || msg.inquiryType || msg.category || 'Billing & Subscription';
                  const supportBody =
                    msg.message || msg.body || msg.inquiryDetail || msg.inquiry_text || '';
                  const attachmentsList: ChatMessageAttachment[] =
                    msg.attachments && msg.attachments.length > 0
                      ? msg.attachments
                      : msg.media_url
                        ? [
                            {
                              url: msg.media_url,
                              thumbnail: msg.thumbnail,
                              file_name: msg.file_name || 'attachment',
                              size_label:
                                msg.size_label ||
                                (typeof msg.file_size === 'string' ? msg.file_size : ''),
                              mime_type: msg.mime_type || '',
                              kind: 'image',
                              type: 'image',
                            },
                          ]
                        : [];

                  return (
                    <div
                      id={`msg-${msg.id}`}
                      key={msg.id}
                      className={`message-bubble-wrapper ${isSentByTrainer ? 'sent' : 'received'} ${isCurrentMatch ? 'search-match-active' : ''}`}
                      style={{ maxWidth: '85%' }}
                    >
                      {!isSentByTrainer && (
                        <div className="message-sender-avatar is-support-avatar">
                          <SupportHeadsetIcon className="support-headset-icon" />
                        </div>
                      )}
                      <div className="message-content-box" style={{ width: '100%' }}>
                        <div className="support-details-card">
                          {/* Row 1: SUBJECT */}
                          <div className="support-card-row">
                            <span className="support-card-label">SUBJECT</span>
                            <span className="support-card-value font-medium">
                              {renderMessageTextWithLinksAndHighlight(
                                supportSubject,
                                isSearchActive ? debouncedSearchQuery : '',
                                `${msg.id}_subject`,
                              )}
                            </span>
                          </div>

                          <div className="support-card-divider" />

                          {/* Row 2: TOPIC */}
                          <div className="support-card-row">
                            <span className="support-card-label">TOPIC</span>
                            <span className="support-card-value">{supportTopic}</span>
                          </div>

                          <div className="support-card-divider" />

                          {/* Row 3: MESSAGE */}
                          <div className="support-card-row">
                            <span className="support-card-label">MESSAGE</span>
                            <div className="support-card-value">
                              {renderMessageTextWithLinksAndHighlight(
                                supportBody,
                                isSearchActive ? debouncedSearchQuery : '',
                                `${msg.id}_body`,
                              )}
                              {(() => {
                                const linkUrls = extractAllUrls(supportBody);
                                if (linkUrls.length > 0) {
                                  const previews =
                                    msg.link_previews ||
                                    (msg.link_preview ? [msg.link_preview] : []);
                                  return (
                                    <div className="chat-link-cards-stack">
                                      {linkUrls.map((linkUrl, idx) => {
                                        const preview =
                                          previews.find((p) => p.url === linkUrl) || previews[idx];
                                        return (
                                          <ChatLinkCard
                                            key={`${msg.id}_support_link_${idx}`}
                                            url={linkUrl}
                                            contextText={supportBody}
                                            initialTitle={preview?.title}
                                            initialDescription={preview?.description}
                                            variant="message"
                                            isSentByTrainer={isSentByTrainer}
                                          />
                                        );
                                      })}
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </div>

                          {/* Row 4: ATTACHMENTS (if any) */}
                          {attachmentsList.length > 0 && (
                            <>
                              <div className="support-card-divider" />
                              <div className="support-card-attachments">
                                <span className="support-card-label">ATTACHMENTS</span>
                                <div className="support-attachments-list">
                                  {attachmentsList.map((att, attIdx) => {
                                    const attUrl = att.url;
                                    const attFileName = att.file_name || `attachment_${attIdx + 1}`;
                                    const rawSize =
                                      att.size_label ||
                                      (att.size_bytes
                                        ? `${(att.size_bytes / (1024 * 1024)).toFixed(1)} MB`
                                        : typeof att.file_size === 'string'
                                          ? att.file_size
                                          : '');
                                    const fileExt = attFileName.includes('.')
                                      ? attFileName.split('.').pop()?.toUpperCase()
                                      : '';
                                    const attSize = rawSize
                                      ? fileExt && !rawSize.toUpperCase().includes(fileExt)
                                        ? `${rawSize} · ${fileExt}`
                                        : rawSize
                                      : fileExt || '';
                                    const attMime = att.mime_type || '';
                                    const isAttImage =
                                      att.kind === 'image' ||
                                      att.type === 'image' ||
                                      attMime.includes('image') ||
                                      isPureImageExtension(attFileName, attMime);
                                    const isAttVideo =
                                      att.kind === 'video' ||
                                      att.type === 'video' ||
                                      attMime.includes('video');
                                    const isAttMedia = isAttImage || isAttVideo;
                                    const meta = getDocTypeMeta(attFileName, attMime);

                                    return (
                                      <div
                                        key={`${msg.id}_att_${attIdx}`}
                                        className="support-attachment-box"
                                        onClick={() => {
                                          if (isAttMedia) {
                                            if (isAttVideo) {
                                              setPreviewVideoUrl(attUrl);
                                            } else {
                                              setPreviewImageUrl(attUrl);
                                            }
                                          } else if (attUrl) {
                                            window.open(attUrl, '_blank');
                                          }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                      >
                                        {isAttMedia ? (
                                          <div className="support-attachment-thumb">
                                            <img
                                              src={att.thumbnail || attUrl}
                                              alt={attFileName}
                                              onError={(e) => {
                                                (e.target as HTMLElement).style.display = 'none';
                                              }}
                                            />
                                            {isAttVideo && (
                                              <div className="play-icon-overlay">
                                                <PlayCircleFilled />
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <div
                                            className="doc-icon-wrapper"
                                            style={{ background: meta.bg, marginRight: '0px' }}
                                          >
                                            {meta.icon}
                                          </div>
                                        )}
                                        <div className="support-attachment-info">
                                          <span className="support-attachment-name">
                                            {attFileName}
                                          </span>
                                          {attSize && (
                                            <span className="support-attachment-size">
                                              {attSize}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        <div className="message-meta">
                          <MessageReadStatus
                            isRead={msg.is_read}
                            isSentByTrainer={isSentByTrainer}
                          />
                          <span>{timeStr}</span>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Render Check-in Card Bubble
                if (isCheckin) {
                  const progress = msg.checkinProgress || '1';
                  let statusLabel = 'Pending confirmation';
                  let statusClass = 'pending';
                  if (progress === '2') {
                    statusLabel = msg.checkinNewSuggest ? 'Counter-Proposal' : 'New Time Proposed';
                    statusClass = 'proposed';
                  } else if (progress === '3') {
                    statusLabel = 'Confirmed';
                    statusClass = 'confirmed';
                  } else if (progress === '4') {
                    statusLabel = 'Attended';
                    statusClass = 'attended';
                  } else if (progress === '5') {
                    statusLabel = 'Declined';
                    statusClass = 'declined';
                  } else if (progress === '6') {
                    statusLabel = 'Cancelled';
                    statusClass = 'cancelled';
                  }

                  const isOnline = msg.checkinType !== 'inPerson';
                  const typeLabel = isOnline ? 'Online via Zoom' : 'In-person';

                  return (
                    <div
                      id={`msg-${msg.id}`}
                      key={msg.id}
                      className={`message-bubble-wrapper received ${isCurrentMatch ? 'search-match-active' : ''}`}
                      style={{ maxWidth: '85%' }}
                    >
                      <div
                        className="message-sender-avatar"
                        style={
                          clientAvatarUrl ? { backgroundImage: `url(${clientAvatarUrl})` } : {}
                        }
                      >
                        {!clientAvatarUrl && getInitials(clientFullName)}
                      </div>
                      <div className="message-content-box" style={{ width: '100%' }}>
                        <div className="checkin-details-card">
                          <div className="checkin-card-header">
                            <div className="header-title-box">
                              <CalendarOutlined className="calendar-icon" />
                              <span className="card-title">Check-in Session</span>
                            </div>
                            <span className={`checkin-status-pill ${statusClass}`}>
                              {statusLabel}
                            </span>
                          </div>

                          <div className="checkin-card-divider" />

                          <div className="checkin-card-body">
                            <div className="checkin-card-row">
                              <span className="checkin-card-label">Date &amp; Time</span>
                              <span className="checkin-card-value">
                                {msg.checkinDate || '—'}
                                {msg.checkinTime
                                  ? ` at ${formatTimeWithAmPm(msg.checkinTime)}`
                                  : ''}
                              </span>
                            </div>

                            <div className="checkin-card-row">
                              <span className="checkin-card-label">Meeting Type</span>
                              <span className="checkin-card-value">{typeLabel}</span>
                            </div>

                            {progress === '2' && (
                              <div className="checkin-proposal-box">
                                <div className="proposal-header">
                                  {msg.checkinNewSuggest
                                    ? 'Client requested another time:'
                                    : 'Trainer proposed alternative:'}
                                </div>
                                <div className="proposal-time">
                                  {msg.checkinNewDate || '—'}
                                  {msg.checkinNewTime
                                    ? ` at ${formatTimeWithAmPm(msg.checkinNewTime)}`
                                    : ''}
                                </div>
                              </div>
                            )}

                            {progress === '3' && msg.zoomMeetingUrl && (
                              <div className="checkin-zoom-box">
                                <span className="zoom-label">Zoom Link:</span>
                                <a
                                  href={msg.zoomMeetingUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="zoom-url"
                                >
                                  {msg.zoomMeetingUrl}
                                </a>
                              </div>
                            )}

                            {progress === '5' && msg.checkinDeclinedReason && (
                              <div className="checkin-declined-box">
                                <span className="decline-label">Decline Reason:</span>
                                <span className="decline-text">
                                  &ldquo;{msg.checkinDeclinedReason}&rdquo;
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="checkin-card-footer">
                            <button
                              type="button"
                              className="btn-view-checkin"
                              onClick={() =>
                                navigate(`/onboarding-trainer/dashboard/messages/checkin/${msg.id}`)
                              }
                            >
                              View Check-in Details
                            </button>
                          </div>
                        </div>

                        <div className="message-meta">
                          <MessageReadStatus
                            isRead={msg.is_read}
                            isSentByTrainer={isSentByTrainer}
                          />
                          <span>{timeStr}</span>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Render Inquiry Card Bubble
                if (isInquiry) {
                  const categoryName =
                    msg.inquiryType || msg.category || msg.inquiry_category || 'General';
                  const messageBody = msg.inquirySubject
                    ? msg.inquiryDetail
                      ? `${msg.inquirySubject}\n\n${msg.inquiryDetail}`
                      : msg.inquirySubject
                    : msg.inquiryDetail ||
                      msg.inquiry_text ||
                      (msg.body !== 'Inquiry sent' ? msg.body : '') ||
                      'Inquiry sent';
                  const inquiryAttachments: ChatMessageAttachment[] =
                    msg.attachments && msg.attachments.length > 0
                      ? msg.attachments
                      : msg.media_url
                        ? [
                            {
                              url: msg.media_url,
                              thumbnail: msg.thumbnail,
                              file_name: msg.file_name || 'Attachment',
                              size_label:
                                msg.size_label ||
                                (typeof msg.file_size === 'string' ? msg.file_size : ''),
                              mime_type: msg.mime_type || '',
                              kind: 'image',
                              type: msg.message_type || 'image',
                            },
                          ]
                        : [];

                  return (
                    <div
                      id={`msg-${msg.id}`}
                      key={msg.id}
                      className={`message-bubble-wrapper received ${isCurrentMatch ? 'search-match-active' : ''}`}
                      style={{ maxWidth: '80%' }}
                    >
                      <div
                        className="message-sender-avatar"
                        style={
                          clientAvatarUrl ? { backgroundImage: `url(${clientAvatarUrl})` } : {}
                        }
                      >
                        {!clientAvatarUrl && getInitials(clientFullName)}
                      </div>
                      <div className="message-content-box" style={{ width: '100%' }}>
                        <div className="inquiry-details-card">
                          <div className="inquiry-card-row">
                            <span className="inquiry-card-label">Category</span>
                            <span
                              className="inquiry-card-value"
                              style={{ textTransform: 'capitalize' }}
                            >
                              {categoryName}
                            </span>
                          </div>
                          <div className="inquiry-card-divider" />
                          <div className="inquiry-card-row">
                            <span className="inquiry-card-label">Message</span>
                            <span className="inquiry-card-value">
                              {renderMessageTextWithLinksAndHighlight(
                                messageBody,
                                isSearchActive ? debouncedSearchQuery : '',
                                msg.id,
                              )}
                              {(() => {
                                const linkUrls = extractAllUrls(messageBody);
                                if (linkUrls.length > 0) {
                                  const previews =
                                    msg.link_previews ||
                                    (msg.link_preview ? [msg.link_preview] : []);
                                  return (
                                    <div className="chat-link-cards-stack">
                                      {linkUrls.map((linkUrl, idx) => {
                                        const preview =
                                          previews.find((p) => p.url === linkUrl) || previews[idx];
                                        return (
                                          <ChatLinkCard
                                            key={`${msg.id}_inquiry_link_${idx}`}
                                            url={linkUrl}
                                            contextText={messageBody}
                                            initialTitle={preview?.title}
                                            initialDescription={preview?.description}
                                            variant="message"
                                            isSentByTrainer={false}
                                          />
                                        );
                                      })}
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </span>
                          </div>
                          {inquiryAttachments.length > 0 && (
                            <>
                              <div className="inquiry-card-divider" />
                              <div className="inquiry-card-attachments">
                                <span className="inquiry-card-label">Attachments</span>
                                <div
                                  className="inquiry-attachments-list"
                                  style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
                                >
                                  {inquiryAttachments.map((att, attIdx) => {
                                    const attUrl = att.url;
                                    const attFileName = att.file_name || `attachment_${attIdx + 1}`;
                                    const attSize =
                                      att.size_label ||
                                      (att.size_bytes
                                        ? `${(att.size_bytes / (1024 * 1024)).toFixed(1)} MB`
                                        : typeof att.file_size === 'string'
                                          ? att.file_size
                                          : '');
                                    const attMime = att.mime_type || '';
                                    const isAttImage =
                                      att.kind === 'image' ||
                                      att.type === 'image' ||
                                      attMime.includes('image') ||
                                      isPureImageExtension(attFileName, attMime);
                                    const isAttVideo =
                                      att.kind === 'video' ||
                                      att.type === 'video' ||
                                      attMime.includes('video');
                                    const isAttMedia = isAttImage || isAttVideo;
                                    const meta = getDocTypeMeta(attFileName, attMime);

                                    return (
                                      <div
                                        key={`${msg.id}_inquiry_att_${attIdx}`}
                                        className="inquiry-attachment-box"
                                        onClick={() => {
                                          if (isAttMedia) {
                                            if (isAttVideo) {
                                              setPreviewVideoUrl(attUrl);
                                            } else {
                                              setPreviewImageUrl(attUrl);
                                            }
                                          } else if (attUrl) {
                                            window.open(attUrl, '_blank');
                                          }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                      >
                                        {isAttMedia ? (
                                          <div className="inquiry-attachment-thumb">
                                            <img
                                              src={att.thumbnail || attUrl}
                                              alt={attFileName}
                                              onError={(e) => {
                                                (e.target as HTMLElement).style.display = 'none';
                                              }}
                                            />
                                            {isAttVideo && (
                                              <div className="play-icon-overlay">
                                                <PlayCircleFilled />
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <div
                                            className="doc-icon-wrapper"
                                            style={{ background: meta.bg, marginRight: '0px' }}
                                          >
                                            {meta.icon}
                                          </div>
                                        )}
                                        <div className="inquiry-attachment-info">
                                          <span className="inquiry-attachment-name">
                                            {attFileName}
                                          </span>
                                          {attSize && (
                                            <span className="inquiry-attachment-size">
                                              {attSize}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        <div className="message-meta">
                          <MessageReadStatus
                            isRead={msg.is_read}
                            isSentByTrainer={isSentByTrainer}
                          />
                          <span>{timeStr}</span>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Audio Message Bubble
                if (msg.message_type === 'audio' || Boolean(msg.audio_url)) {
                  return (
                    <div
                      id={`msg-${msg.id}`}
                      key={msg.id}
                      data-message-id={msg.id}
                      data-unread={!isSentByTrainer && !msg.is_read}
                      className={`message-bubble-wrapper ${isSentByTrainer ? 'sent' : 'received'} ${isCurrentMatch ? 'search-match-active' : ''}`}
                    >
                      {!isSentByTrainer && (
                        <div
                          className="message-sender-avatar"
                          style={
                            clientAvatarUrl ? { backgroundImage: `url(${clientAvatarUrl})` } : {}
                          }
                        >
                          {!clientAvatarUrl && getInitials(clientFullName)}
                        </div>
                      )}
                      <div className="message-content-box">
                        <AudioMessageBubble
                          message={msg}
                          isSentByTrainer={isSentByTrainer}
                          timeStr={timeStr}
                          renderTextWithLinks={(text) =>
                            renderMessageTextWithLinksAndHighlight(
                              text,
                              isSearchActive ? debouncedSearchQuery : '',
                            )
                          }
                        />
                      </div>
                    </div>
                  );
                }

                // Determine if it's purely a Media Message (Images / Videos) or Document Message
                const hasAttachments = Boolean(msg.attachments && msg.attachments.length > 0);

                // Document / File Message Bubble: Any message sent as 'file', 'pdf', or containing document attachments
                const hasDocAttachments =
                  msg.message_type === 'file' ||
                  msg.message_type === 'pdf' ||
                  (hasAttachments &&
                    msg.attachments!.some(
                      (a) =>
                        a.kind === 'file' ||
                        a.type === 'document' ||
                        (!isPureImageExtension(a.file_name, a.mime_type) &&
                          !isPureVideoExtension(a.file_name, a.mime_type) &&
                          a.type !== 'image' &&
                          a.type !== 'video'),
                    )) ||
                  (!hasAttachments &&
                    Boolean(msg.media_url) &&
                    !msg.audio_url &&
                    msg.message_type !== 'inquiry' &&
                    msg.message_type !== 'audio' &&
                    msg.message_type !== 'image' &&
                    msg.message_type !== 'video' &&
                    !isPureImageExtension(msg.file_name) &&
                    !isPureVideoExtension(msg.file_name));

                // Media Gallery: for message_type === 'image' | 'video' (or media attachments) and not a doc message
                const isMediaMsg =
                  !hasDocAttachments &&
                  (msg.message_type === 'image' ||
                    msg.message_type === 'video' ||
                    (hasAttachments &&
                      msg.attachments!.every(
                        (a) =>
                          a.kind === 'media' ||
                          a.type === 'image' ||
                          a.type === 'video' ||
                          isPureImageExtension(a.file_name, a.mime_type) ||
                          isPureVideoExtension(a.file_name, a.mime_type),
                      )) ||
                    (!hasAttachments &&
                      Boolean(msg.media_url) &&
                      !msg.audio_url &&
                      msg.message_type !== 'inquiry' &&
                      msg.message_type !== 'audio' &&
                      (isPureImageExtension(msg.file_name) ||
                        isPureVideoExtension(msg.file_name))));

                if (hasDocAttachments) {
                  const docAttachments =
                    msg.attachments && msg.attachments.length > 0
                      ? msg.attachments
                      : msg.media_url
                        ? [
                            {
                              url: msg.media_url,
                              kind: 'file' as const,
                              type: 'document' as const,
                              file_name: msg.file_name || 'Document',
                              size_label:
                                msg.size_label ||
                                (typeof msg.file_size === 'string' ? msg.file_size : undefined),
                            },
                          ]
                        : [];

                  return (
                    <div
                      id={`msg-${msg.id}`}
                      key={msg.id}
                      data-message-id={msg.id}
                      data-unread={!isSentByTrainer && !msg.is_read}
                      className={`message-bubble-wrapper doc-message-bubble-wrapper ${isSentByTrainer ? 'sent' : 'received'} ${isCurrentMatch ? 'search-match-active' : ''}`}
                    >
                      {!isSentByTrainer && (
                        <div
                          className="message-sender-avatar"
                          style={
                            clientAvatarUrl ? { backgroundImage: `url(${clientAvatarUrl})` } : {}
                          }
                        >
                          {!clientAvatarUrl && getInitials(clientFullName)}
                        </div>
                      )}
                      <div className="message-content-box">
                        <div className="doc-bubble-card">
                          <div className="doc-items-container">
                            {docAttachments.map((att, idx) => {
                              const meta = getDocTypeMeta(att.file_name, att.mime_type);
                              return (
                                <a
                                  key={idx}
                                  href={att.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download={att.file_name || 'document'}
                                  className="chat-doc-item"
                                >
                                  <div className="doc-icon-wrapper" style={{ background: meta.bg }}>
                                    {meta.icon}
                                  </div>
                                  <div className="doc-info-wrapper">
                                    <span className="doc-name" title={att.file_name}>
                                      {att.file_name || 'Document'}
                                    </span>
                                    <span className="doc-size">
                                      {att.size_label ||
                                        (att.size_bytes
                                          ? formatDocFileSize(att.size_bytes)
                                          : meta.label)}
                                    </span>
                                  </div>
                                  <div className="doc-download-icon">
                                    <DownloadOutlined />
                                  </div>
                                </a>
                              );
                            })}
                          </div>
                          {Boolean(msg.body && msg.body.trim()) && (
                            <div className="doc-caption-text">
                              {renderMessageTextWithLinksAndHighlight(
                                msg.body,
                                isSearchActive ? debouncedSearchQuery : '',
                                msg.id,
                              )}
                            </div>
                          )}
                        </div>
                        <div className="message-meta">
                          <MessageReadStatus
                            isRead={msg.is_read}
                            isSentByTrainer={isSentByTrainer}
                          />
                          <span>{timeStr}</span>
                        </div>
                      </div>
                    </div>
                  );
                }

                if (isMediaMsg) {
                  const mediaAttachments =
                    msg.attachments && msg.attachments.length > 0
                      ? msg.attachments.filter(
                          (a) =>
                            a.kind === 'media' ||
                            a.type === 'image' ||
                            a.type === 'video' ||
                            isPureImageExtension(a.file_name, a.mime_type) ||
                            isPureVideoExtension(a.file_name, a.mime_type),
                        )
                      : msg.media_url
                        ? [
                            {
                              url: msg.media_url,
                              kind: 'media' as const,
                              type: (msg.message_type === 'video' ||
                              isPureVideoExtension(msg.file_name)
                                ? 'video'
                                : 'image') as 'image' | 'video',
                              file_name: msg.file_name,
                              size_label:
                                msg.size_label ||
                                (typeof msg.file_size === 'string' ? msg.file_size : undefined),
                            },
                          ]
                        : [];

                  return (
                    <div
                      id={`msg-${msg.id}`}
                      key={msg.id}
                      data-message-id={msg.id}
                      data-unread={!isSentByTrainer && !msg.is_read}
                      className={`message-bubble-wrapper image-message-bubble-wrapper ${isSentByTrainer ? 'sent' : 'received'} ${isCurrentMatch ? 'search-match-active' : ''}`}
                    >
                      {!isSentByTrainer && (
                        <div
                          className="message-sender-avatar"
                          style={
                            clientAvatarUrl ? { backgroundImage: `url(${clientAvatarUrl})` } : {}
                          }
                        >
                          {!clientAvatarUrl && getInitials(clientFullName)}
                        </div>
                      )}
                      <div className="message-content-box">
                        <div className="image-bubble-card">
                          <div
                            className={`chat-images-grid-container count-${Math.min(mediaAttachments.length, 5)}`}
                          >
                            {mediaAttachments.map((att, idx) => {
                              const isVideoItem =
                                att.type === 'video' ||
                                isPureVideoExtension(att.file_name, att.mime_type);
                              if (isVideoItem) {
                                return (
                                  <div
                                    key={idx}
                                    className="chat-image-item chat-video-item"
                                    onClick={() => setPreviewVideoUrl(att.url)}
                                    role="button"
                                    tabIndex={0}
                                    title="Click to play video"
                                  >
                                    <video src={att.url} preload="metadata" playsInline muted />
                                    <div className="video-play-overlay">
                                      <PlayCircleFilled className="video-play-icon" />
                                    </div>
                                    {(att.size_label || att.size_bytes) && (
                                      <span className="video-duration-size-badge">
                                        {att.size_label || formatDocFileSize(att.size_bytes || 0)}
                                      </span>
                                    )}
                                  </div>
                                );
                              }
                              return (
                                <div
                                  key={idx}
                                  className="chat-image-item"
                                  onClick={() => setPreviewImageUrl(att.url)}
                                  role="button"
                                  tabIndex={0}
                                >
                                  <img
                                    src={att.url}
                                    alt={att.file_name || 'attached image'}
                                    loading="lazy"
                                  />
                                </div>
                              );
                            })}
                          </div>
                          {Boolean(msg.body && msg.body.trim()) && (
                            <div className="image-caption-text">
                              {renderMessageTextWithLinksAndHighlight(
                                msg.body,
                                isSearchActive ? debouncedSearchQuery : '',
                                msg.id,
                              )}
                            </div>
                          )}
                        </div>
                        <div className="message-meta">
                          <MessageReadStatus
                            isRead={msg.is_read}
                            isSentByTrainer={isSentByTrainer}
                          />
                          <span>{timeStr}</span>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Normal Chat Bubble
                return (
                  <div
                    id={`msg-${msg.id}`}
                    key={msg.id}
                    data-message-id={msg.id}
                    data-unread={!isSentByTrainer && !msg.is_read}
                    className={`message-bubble-wrapper ${isSentByTrainer ? 'sent' : 'received'} ${isCurrentMatch ? 'search-match-active' : ''}`}
                  >
                    {!isSentByTrainer && (
                      <div
                        className="message-sender-avatar"
                        style={
                          clientAvatarUrl ? { backgroundImage: `url(${clientAvatarUrl})` } : {}
                        }
                      >
                        {!clientAvatarUrl && getInitials(clientFullName)}
                      </div>
                    )}
                    <div className="message-content-box">
                      <div className="message-bubble">
                        <div className="bubble-body">
                          {renderMessageTextWithLinksAndHighlight(
                            msg.body,
                            isSearchActive ? debouncedSearchQuery : '',
                            msg.id,
                          )}
                        </div>
                        {(() => {
                          const linkUrls = extractAllUrls(msg.body);
                          if (linkUrls.length > 0) {
                            const previews =
                              msg.link_previews || (msg.link_preview ? [msg.link_preview] : []);
                            return (
                              <div className="chat-link-cards-stack">
                                {linkUrls.map((linkUrl, idx) => {
                                  const preview =
                                    previews.find((p) => p.url === linkUrl) || previews[idx];
                                  return (
                                    <ChatLinkCard
                                      key={`${msg.id}_link_${idx}`}
                                      url={linkUrl}
                                      contextText={msg.body}
                                      initialTitle={preview?.title}
                                      initialDescription={preview?.description}
                                      variant="message"
                                      isSentByTrainer={isSentByTrainer}
                                    />
                                  );
                                })}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      <div className="message-meta">
                        <MessageReadStatus isRead={msg.is_read} isSentByTrainer={isSentByTrainer} />
                        <span>{timeStr}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Chat Composer */}
      <div
        className={`chat-composer-container ${isChatReadOnly ? 'is-readonly' : ''} ${!inquiryMessageId && isSearchActive ? 'hidden-on-mobile-search' : ''}`}
      >
        {/* Hidden File Input for Media (Images & Videos) */}
        <input
          ref={mediaFileInputRef}
          type="file"
          accept={[...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES].join(',')}
          multiple
          style={{ display: 'none' }}
          onChange={handleMediaFileChange}
        />

        {/* Hidden File Input for Documents */}
        <input
          ref={docFileInputRef}
          type="file"
          accept={ALLOWED_DOCUMENT_TYPES.join(',')}
          multiple
          style={{ display: 'none' }}
          onChange={handleDocFileChange}
        />

        {isChatReadOnly ? (
          <div className="chat-composer-readonly">
            <LockOutlined className="readonly-icon" />
            <span className="readonly-text">
              This conversation is read-only because the client relationship has ended.
            </span>
          </div>
        ) : audioRecorder.isRecording ? (
          <AudioRecorderInline recorder={audioRecorder} maxDurationSeconds={300} />
        ) : (
          <>
            {/* Audio Preview if audio recorded */}
            {Boolean(audioRecorder.audioUrl) && (
              <div className="composer-audio-preview-container">
                <AudioPlayer
                  src={audioRecorder.audioUrl!}
                  durationSeconds={audioRecorder.duration}
                  theme="preview"
                />
                <button
                  type="button"
                  className="delete-preview-btn"
                  onClick={audioRecorder.clearAudio}
                  title="Discard voice note"
                >
                  <DeleteOutlined />
                </button>
              </div>
            )}

            {/* Attached Media (Images & Videos) Preview Row */}
            {attachedMedia.length > 0 && (
              <div className="composer-image-previews-container">
                <div className="composer-previews-scroll-row">
                  <div className="composer-thumbnails-list">
                    {attachedMedia.map((m) => (
                      <div
                        key={m.id}
                        className="attached-image-thumb-card attached-media-thumb-card"
                        onClick={() => {
                          const targetUrl = m.storageUrl || m.previewUrl;
                          if (m.mediaType === 'video') {
                            setPreviewVideoUrl(targetUrl);
                          } else {
                            setPreviewImageUrl(targetUrl);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        title={
                          m.mediaType === 'video'
                            ? 'Click to preview video'
                            : 'Click to preview image'
                        }
                      >
                        {m.mediaType === 'video' ? (
                          <div className="composer-video-thumb-wrapper">
                            <video src={m.previewUrl} preload="metadata" muted />
                            <div className="composer-video-play-indicator">
                              <PlayCircleFilled />
                            </div>
                          </div>
                        ) : (
                          <img src={m.previewUrl} alt={m.file.name} />
                        )}
                        {m.isUploading && (
                          <div className="upload-overlay">
                            <LoadingOutlined spin />
                          </div>
                        )}
                        {m.uploadError && (
                          <div className="upload-error-overlay" title="Failed to upload">
                            <WarningOutlined />
                          </div>
                        )}
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveAttachedMedia(m);
                          }}
                          title="Remove"
                        >
                          <CloseOutlined />
                        </button>
                      </div>
                    ))}
                  </div>
                  <span
                    className={`images-count-pill ${
                      attachedMedia.length >= MAX_MEDIA_COUNT ? 'limit-reached' : ''
                    }`}
                  >
                    {attachedMedia.length} / {MAX_MEDIA_COUNT}
                  </span>
                </div>
                {mediaLimitWarning && (
                  <div className="composer-limit-warning-banner">
                    <WarningOutlined className="warning-icon" />
                    <span className="warning-text">{mediaLimitWarning}</span>
                    <button
                      type="button"
                      className="dismiss-warning-btn"
                      onClick={() => setMediaLimitWarning(null)}
                      title="Dismiss warning"
                    >
                      <CloseOutlined />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Attached Documents Preview Row */}
            {attachedDocs.length > 0 && (
              <div className="composer-doc-previews-container">
                <div className="composer-previews-scroll-row">
                  <div className="composer-docs-list">
                    {attachedDocs.map((docItem) => {
                      const meta = getDocTypeMeta(docItem.file.name, docItem.file.type);
                      return (
                        <div key={docItem.id} className="attached-doc-card">
                          <div className="doc-card-icon">{meta.icon}</div>
                          <div className="doc-card-info">
                            <span className="doc-card-name" title={docItem.file.name}>
                              {docItem.file.name}
                            </span>
                            <span className="doc-card-size">
                              {formatDocFileSize(docItem.sizeBytes)}
                            </span>
                          </div>
                          {docItem.isUploading && (
                            <div className="doc-upload-spin">
                              <LoadingOutlined spin />
                            </div>
                          )}
                          {docItem.uploadError && (
                            <div className="doc-upload-error" title="Failed to upload">
                              <WarningOutlined />
                            </div>
                          )}
                          <button
                            type="button"
                            className="remove-doc-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveAttachedDoc(docItem);
                            }}
                            title="Remove file"
                          >
                            <CloseOutlined />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <span
                    className={`docs-count-pill ${
                      attachedDocs.length >= MAX_DOCS_COUNT ? 'limit-reached' : ''
                    }`}
                  >
                    {attachedDocs.length} / {MAX_DOCS_COUNT}
                  </span>
                </div>
                {docLimitWarning && (
                  <div className="composer-limit-warning-banner">
                    <WarningOutlined className="warning-icon" />
                    <span className="warning-text">{docLimitWarning}</span>
                    <button
                      type="button"
                      className="dismiss-warning-btn"
                      onClick={() => setDocLimitWarning(null)}
                      title="Dismiss warning"
                    >
                      <CloseOutlined />
                    </button>
                  </div>
                )}
              </div>
            )}

            {inquiryMessageId && (
              <div className="composer-inquiry-title mobile-only">Your response</div>
            )}

            <div className="chat-composer-row">
              <div className="chat-composer-input-pill">
                <textarea
                  ref={textareaRef}
                  className="chat-textarea"
                  placeholder={
                    audioRecorder.audioBlob
                      ? 'Add optional message with voice note...'
                      : attachedMedia.length > 0
                        ? 'Add optional message with photos/videos...'
                        : attachedDocs.length > 0
                          ? 'Add optional message with files...'
                          : inquiryMessageId
                            ? 'Write your response...'
                            : 'Write a message...'
                  }
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
                      setTimeout(() => {
                        window.scrollTo(0, 0);
                        if (document.body) document.body.scrollTop = 0;
                        if (document.documentElement) document.documentElement.scrollTop = 0;
                        const scrollable = document.getElementById('scrollableDashboardMain');
                        if (scrollable) {
                          scrollable.scrollTop = 0;
                        }
                        if (messagesContainerRef.current) {
                          messagesContainerRef.current.scrollTop =
                            messagesContainerRef.current.scrollHeight;
                        }
                      }, 100);
                    }
                  }}
                  onBlur={() => {
                    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
                      window.scrollTo(0, 0);
                      if (document.body) document.body.scrollTop = 0;
                      if (document.documentElement) document.documentElement.scrollTop = 0;
                      const scrollable = document.getElementById('scrollableDashboardMain');
                      if (scrollable) {
                        scrollable.scrollTop = 0;
                      }
                    }
                  }}
                  rows={1}
                />
                <Popover
                  open={Boolean(audioRecorder.errorMessage)}
                  onOpenChange={(open) => {
                    if (!open) {
                      audioRecorder.clearError();
                    }
                  }}
                  placement="topRight"
                  trigger={[]}
                  color="#111422"
                  rootClassName="mic-error-popover-overlay"
                  overlayClassName="mic-error-popover-overlay"
                  content={
                    <div className="mic-error-popover-content">
                      <div className="mic-error-header">
                        <WarningOutlined className="mic-error-icon" />
                        <span className="mic-error-title">No microphone detected</span>
                        <button
                          type="button"
                          className="mic-error-close-btn"
                          onClick={audioRecorder.clearError}
                          title="Dismiss"
                        >
                          <CloseOutlined />
                        </button>
                      </div>
                      <p className="mic-error-desc">
                        {audioRecorder.errorMessage ||
                          'No microphone was detected on this device. Please connect a microphone and try again.'}
                      </p>
                      <div className="mic-error-actions">
                        <button
                          type="button"
                          className="mic-error-btn-dismiss"
                          onClick={audioRecorder.clearError}
                        >
                          Got it
                        </button>
                      </div>
                    </div>
                  }
                >
                  <Tooltip
                    title={
                      audioRecorder.errorMessage
                        ? ''
                        : attachedMedia.length > 0
                          ? 'Remove attached photos/videos to record audio'
                          : attachedDocs.length > 0
                            ? 'Remove attached files to record audio'
                            : 'Record voice note'
                    }
                    placement="top"
                  >
                    <span>
                      <button
                        className="mic-icon-btn"
                        type="button"
                        disabled={attachedMedia.length > 0 || attachedDocs.length > 0}
                        onClick={() => {
                          if (
                            attachedMedia.length === 0 &&
                            attachedDocs.length === 0 &&
                            !audioRecorder.isRecording
                          ) {
                            audioRecorder.clearError();
                            audioRecorder.startRecording();
                          }
                        }}
                      >
                        <InputRecordIcon />
                      </button>
                    </span>
                  </Tooltip>
                </Popover>
              </div>

              <div className="composer-actions-outside">
                <Popover
                  content={
                    <div className="emoji-picker-popover-content">
                      <EmojiPicker
                        theme={Theme.DARK}
                        onEmojiClick={handleEmojiClick}
                        searchPlaceHolder="Search emoji..."
                        width={340}
                        height={380}
                        lazyLoadEmojis
                        previewConfig={{ showPreview: false }}
                        searchDisabled={false}
                        autoFocusSearch={false}
                        style={
                          {
                            border: 'none',
                            backgroundColor: '#111422',
                            boxShadow: 'none',
                            '--epr-bg-color': '#111422',
                            '--epr-picker-border-color': 'transparent',
                            '--epr-search-input-padding': '0 32px 0 38px',
                            '--epr-search-bar-inner-padding': '12px',
                          } as React.CSSProperties
                        }
                      />
                    </div>
                  }
                  trigger="click"
                  open={showEmojiPicker}
                  onOpenChange={setShowEmojiPicker}
                  placement="topLeft"
                  overlayClassName="chat-emoji-popover-overlay"
                  arrow={false}
                  destroyOnHidden={false}
                >
                  <Tooltip title="Emoji" placement="top">
                    <button
                      className={`action-icon-btn ${showEmojiPicker ? 'active' : ''}`}
                      type="button"
                    >
                      <InputEmojiIcon />
                    </button>
                  </Tooltip>
                </Popover>

                <Tooltip
                  title={
                    audioRecorder.isRecording || audioRecorder.audioBlob
                      ? 'Remove voice note to attach photos/videos'
                      : attachedDocs.length > 0
                        ? 'Remove attached files to attach photos/videos'
                        : 'Attach photos or videos (max 5, photos up to 3MB, videos up to 50MB)'
                  }
                  placement="top"
                >
                  <span>
                    <button
                      className={`action-icon-btn ${attachedMedia.length > 0 ? 'active-media' : ''}`}
                      type="button"
                      disabled={
                        audioRecorder.isRecording ||
                        Boolean(audioRecorder.audioBlob) ||
                        attachedDocs.length > 0
                      }
                      onClick={() => {
                        if (
                          !audioRecorder.isRecording &&
                          !audioRecorder.audioBlob &&
                          attachedDocs.length === 0
                        ) {
                          if (attachedMedia.length >= MAX_MEDIA_COUNT) {
                            setMediaLimitWarning(
                              `Maximum of ${MAX_MEDIA_COUNT} photos/videos allowed per message.`,
                            );
                            return;
                          }
                          mediaFileInputRef.current?.click();
                        }
                      }}
                    >
                      <InputImagesIcon />
                    </button>
                  </span>
                </Tooltip>

                <Tooltip
                  title={
                    attachedMedia.length > 0
                      ? 'Remove attached photos/videos to attach files'
                      : audioRecorder.isRecording || audioRecorder.audioBlob
                        ? 'Remove voice note to attach files'
                        : 'Attach files (max 5, up to 3MB each)'
                  }
                  placement="top"
                >
                  <span>
                    <button
                      className={`action-icon-btn ${attachedDocs.length > 0 ? 'active-media' : ''}`}
                      type="button"
                      disabled={
                        attachedMedia.length > 0 ||
                        audioRecorder.isRecording ||
                        Boolean(audioRecorder.audioBlob)
                      }
                      onClick={() => {
                        if (
                          attachedMedia.length === 0 &&
                          !audioRecorder.isRecording &&
                          !audioRecorder.audioBlob
                        ) {
                          if (attachedDocs.length >= MAX_DOCS_COUNT) {
                            setDocLimitWarning(
                              `Maximum of ${MAX_DOCS_COUNT} files allowed per message.`,
                            );
                            return;
                          }
                          docFileInputRef.current?.click();
                        }
                      }}
                    >
                      <InputAttachmentsIcon />
                    </button>
                  </span>
                </Tooltip>

                <span className="composer-hint-mobile">
                  {inputText.length > 2000 ? (
                    <span style={{ color: '#ff4d4f', fontWeight: '500' }}>
                      Limit exceeded ({inputText.length}/2000)
                    </span>
                  ) : (
                    'Press Enter for new line'
                  )}
                </span>
              </div>
              <button
                className="send-button"
                onClick={handleSendMessage}
                disabled={
                  (!inputText.trim() &&
                    !audioRecorder.audioBlob &&
                    attachedMedia.length === 0 &&
                    attachedDocs.length === 0) ||
                  inputText.length > 2000 ||
                  sending ||
                  attachedMedia.some((m) => m.isUploading) ||
                  attachedDocs.some((d) => d.isUploading)
                }
                type="button"
              >
                {sending ? (
                  <LoadingOutlined />
                ) : (
                  <span className="send-btn-icon-wrap">
                    <InputSendIcon />
                  </span>
                )}
              </button>
            </div>
            <div
              className="composer-footer-helper desktop-only"
              style={{ justifyContent: 'space-between' }}
            >
              <span>Press Enter to send · Shift+Enter for new line</span>
              {inputText.length > 2000 && (
                <span style={{ color: '#ff4d4f', fontWeight: '500' }}>
                  Character limit exceeded ({inputText.length}/2000)
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Mobile Bottom Search Match Navigation Bar */}
      {!inquiryMessageId && isSearchActive && debouncedSearchQuery.trim() && (
        <div className="mobile-search-bottom-bar mobile-only">
          <button
            className="search-nav-btn"
            disabled={
              searchMatches.length === 0 ||
              currentMatchIndex >= searchMatches.length - 1 ||
              isFetchingContext
            }
            onClick={() =>
              setCurrentMatchIndex((prev) => Math.min(searchMatches.length - 1, prev + 1))
            }
            aria-label="Previous match (older)"
            title="Previous message (older)"
          >
            <LeftOutlined />
          </button>
          <div className="search-match-text">
            {isSearchingFullHistory ? (
              <span>Searching all history...</span>
            ) : isFetchingContext ? (
              <span>Loading message context...</span>
            ) : searchMatches.length > 0 ? (
              <>
                <span className="current-match">{currentMatchIndex + 1}</span> of{' '}
                <span className="total-matches">{searchMatches.length}</span> matches
              </>
            ) : (
              <span>0 matches</span>
            )}
          </div>
          <button
            className="search-nav-btn"
            disabled={searchMatches.length === 0 || currentMatchIndex <= 0 || isFetchingContext}
            onClick={() => setCurrentMatchIndex((prev) => Math.max(0, prev - 1))}
            aria-label="Latest match (newer)"
            title="Latest message (newer)"
          >
            <RightOutlined />
          </button>
        </div>
      )}

      {/* Image Preview Modal */}
      <FullScreenImageModal
        isOpen={Boolean(previewImageUrl)}
        onClose={() => setPreviewImageUrl(null)}
        imageUrl={previewImageUrl || ''}
      />

      {/* Video Preview Modal */}
      <FullScreenVideoModal
        isOpen={Boolean(previewVideoUrl)}
        onClose={() => setPreviewVideoUrl(null)}
        videoUrl={previewVideoUrl || ''}
      />

      {/* Inquiry Status Confirmation Modal */}
      <Modal
        open={showResolveConfirmModal}
        onCancel={() => !isUpdatingResolveStatus && setShowResolveConfirmModal(false)}
        footer={null}
        closable={false}
        centered
        width={400}
        rootClassName="logout-modal"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="logout-modal-content">
          <h2>{isInquiryResolved ? 'Reopen inquiry?' : 'Mark as resolved?'}</h2>
          <p>
            {isInquiryResolved
              ? 'Are you sure you want to unmark this inquiry as resolved and reopen it?'
              : 'Are you sure you want to mark this inquiry as resolved?'}
          </p>
          <div className="logout-actions">
            <button
              className="btn-logout"
              onClick={handleConfirmToggleResolve}
              disabled={isUpdatingResolveStatus}
              style={{
                background: isInquiryResolved ? '#ffffff' : '#FFA100',
                color: '#04051B',
              }}
            >
              {isUpdatingResolveStatus ? (
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 16, color: '#04051B' }} spin />}
                />
              ) : isInquiryResolved ? (
                'Reopen inquiry'
              ) : (
                'Mark as resolved'
              )}
            </button>
            <button
              className="btn-cancel"
              onClick={() => setShowResolveConfirmModal(false)}
              disabled={isUpdatingResolveStatus}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      <SendFinalOfferModal
        isOpen={isSendOfferModalOpen}
        onClose={() => setIsSendOfferModalOpen(false)}
        publishedPackages={publishedPackages}
        initialPackageId={selectedRequestMessage?.package_id}
        initialStartDate={selectedRequestMessage?.start_date}
        onConfirmSend={handleConfirmSendOffer}
        onOpenDocument={handleOpenDocumentTemplate}
        isSending={isSendingOffer}
      />

      <InviteFreeCallModal
        isOpen={isInviteCallModalOpen}
        onClose={() => setIsInviteCallModalOpen(false)}
        clientName={clientFullName}
        scheduleData={trainerFormattedSchedule}
        onChangeSchedule={() => {
          setIsInviteCallModalOpen(false);
          navigate('/onboarding-trainer/dashboard/profile?tab=settings&section=schedule');
        }}
        onConfirmSend={handleConfirmSendInvitation}
        isSending={isSendingInvitation}
      />

      {/* Modals & Sub-views */}
      {showReportView && (
        <ReportIssueView
          chatId={chatId || ''}
          trainerUid={trainerActualUid}
          clientUid={clientUid}
          onClose={() => setShowReportView(false)}
          onBlocked={() => {
            setShowReportView(false);
            navigate('/onboarding-trainer/dashboard/messages', { replace: true });
          }}
        />
      )}

      {activeView === 'media_files_links' && (
        <MediaLinksFilesView
          chatId={chatId}
          inquiryId={inquiryMessageId || undefined}
          messages={messages}
          initialTab={initialMLFTab}
          onBack={() => setActiveView('chat')}
        />
      )}
    </div>
  );
};
